import React, { useState, useEffect, useRef } from 'react';
import './Notes.scss';
import { useAuth } from '../../contexts/AuthContext';

// Define the note topic interface
interface NoteTopic {
  id: string;
  title: string;
  color: string;
  content: string;
}

// Define user notes interface
interface UserNote {
  id: string;
  title: string;
  content: string;
  createdAt: string;
}

const Notes: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<NoteTopic | UserNote | null>(null);
  const [userNotes, setUserNotes] = useState<UserNote[]>([]);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const noteContentRef = useRef<HTMLDivElement>(null);

  // Pre-defined note topics
  const noteTopics: NoteTopic[] = [
    // --- INTRODUCTION ---
    {
      id: "introduction",
      title: "Introduction",
      color: "#7F8C8D", // Gray
      content: `# INTRODUCTION\n\nHey PLABBer!\n\nSo, you're tackling PLAB 2? Good on you! It's a unique exam, and while it *can* feel daunting, it's absolutely passable with the right mindset and preparation. You might hear about various approaches, including academies. They can offer structure, but sometimes the sheer volume of information can feel overwhelming, and the focus might stray from what truly matters on the day.\n\nWhat often gets missed is the '*BIG PICTURE*'. Understanding this fundamental perspective is what I believe makes the difference. You're already a doctor – you know how to assess, diagnose, and manage patients. PLAB 2 tests these core skills in timed scenarios under observation. It's about demonstrating safe, effective, and *empathetic* practice within the NHS context.\n\nThe key isn't just knowing *what* to do, but *how* to interact and communicate effectively – that's a huge part of the '*BIG PICTURE*'. It's about showing you're a *safe doctor* through your clinical skills and, crucially, your interpersonal skills. The British culture values *politeness*, especially from professionals like doctors, and this exam reflects that.\n\nWhile understanding concepts from first principles is vital (as cramming often fails under pressure), remember that *consistent practice* of stations is non-negotiable. Running through scenarios helps build confidence and makes applying your knowledge smoother on the day.\n\nMy aim here is to offer a different perspective, focusing on that '*BIG PICTURE*'. I want to help you grasp the core principles, build confidence, and ultimately, demonstrate the safe, competent, and communicative doctor you are. Let's get started! Click 'Next' to continue. Good luck, fam!\n\nDr. George Edeh\n\n*Founder of SimPLAB2*`
    },
    // --- BRITISH ENGLISH ---
    {
      id: "british-english",
      title: "British English",
      color: "#D35400", // Orange-Red
      content: `# BRITISH ENGLISH

## SYMPTOMS/SIGNS (History)
- Knackered - tired
- Feeling Sick - nausea
- Being Sick - vomiting
- The runs - Diarrhoea (you can still say diarrhoea)
- Fever - fever/running temperature
- Cold - chills
- Sneezing - snorty
- Runny nose - flu symptoms
- Seizure - fits
- Anger - temper tantrums
- Stool - poo
- Urine - pee/wee
- To Urinate - going to the loo (e.g *have you been going to the loo more often?*)
- Drugs - recreational drugs eg heroine, cocaine
- Medicine - medically prescribed substances eg paracetamol
- Dizziness - ask if feeling *lightheadedness* or feeling of room *spinning* around
- Vertigo - feeling of the room spinning around them
- Epistaxis - nose bleeding
- Hematemesis - throwing up blood
- Hematochezia - passing blood through back passage
- Maleana - passing dark poo
- Dysuria - problems with water works
- Erectile dysfunction - problem with your water works pipe/inability to "perform"
- Obesity - obesity/on the big side (NOTE: DO NOT SAY "FAT". If they ask you are they fat? Tell them such words are prohibited in NHS)
- Gas - passing wind
- Lymphadenopathy - lumps and bumps
- Weight loss - weight loss
- Anorexia - loss of appetite
- Night Sweats - sweating excessively at night
- Chest Pain - chest pain
- Palpitations - fluttering sensation
- Fast Beats - heart racing
- Vaccination - jabs (is your child up to date your jabs?)
- Development milestones - red book (are you happy with *his* red book?)
- Pfannensteil incision - 2cm above the ***bikini line***

---

## EXAMINATION
*Thank you for talking to me; at this point I will like to examine you.*
*I will be checking your vitals (or observation charts), do a general physical exam, and specifically check your chest, heart (or whatever system involved).*

**NB:** *DO NOT SAY HEAD-TO-TOE!*

**Privacy** - I will do all these in the presence of a ***chaperone*** and I will ensure your ***privacy...***

**Consent** - *Do I have your consent to proceed? (get a response before you continue)*

**BMI** - I will be checking your weight and height and calculate your *weight-height ratio*.

**Postural Hypotension** - I will check your BP on lying down and standing

**Ankle-Brachial Index** - I will compare the blood pressure in your arm and your legs

**Dix-Hallpike maneuver** - I will do a special examination called ....

**Murphy's Sign** - I will do a special examination on your right upper tummy to check for pain... called...

**Brudzunski & Kernig's** - I will examine your neck for signs of stiffness called ....

**Complete Exam eg Chest/Abdomen** - I will be *look, touch, tap and listen.*

**DRE** - I will be putting my gloved finger through your back passage to check the ***pea-sized organ*** called prostate

**Ear Exam** - I will examine your ears. I will be looking and touching. I will shine light into your ears with a special instrument called otoscope.

**Tuning Fork Tests** - I will be using this special instrument called "tuning fork" to check your hearing.

**Eye Exam** - I will examine your eyes. I will be looking and touching. I will shine light into your eyes with a special instrument called ophthalmoscope.

**VA & VF tests** - I will check how far and how wide you can see

**Scrotal Exam** - I will examine your scrotal sac/testicles. I will be looking, touching and listening. I will also do some special tests to shine light on your sacs. I will complete this exam with by checking your tummy and your back passage.

**OBS GRAVID ABD** - I will be looking, touching and listening with a special instrument called ***pinard stethoscope.***

## PARTS OF THE BODY
- Vagina - front passage
- Penis - penis/male organ
- Anus - back passage
- Abdomen - tummy
- Oesophagus - food pipe
- Trachea - wind pipe

## INVESTIGATIONS
**Clotting Profile** - test to check how thick or thin your blood is

**FBC** - blood counts

**LFT/KFT** - test to check how your liver and kidney functions

**Electrolytes** - special chemical elements in your blood

**Cardiac enzyme** - special heart attack markers

**ECG** - heart tracing

**CXR** - chest xray

**ABG** - blood gases

**Colonoscopy** - special camera tube through your back passage

**Endoscopy** - special camera tube through your food pipe

**Urinalysis** - urine test

**RBG** - finger prick test for sugar

**Hormone profile** - hormone tests

**Lipid profile** - cholesterol levels

**CT/MRI** - a special scan of your brain (*any organ*) called CT/MRI

**USS** - scan of your tummy (*any organ*) using sounds

## EXPLAINING DIAGNOSES
*Find the simplest & shortest possible explanation that makes sense*

**Microorganisms** - bugs

**Cancer** - cancer

**Dysplastic changes** - some cells in the... have become slightly abnormal...

## TREATMENT
Antibiotics according to the hospital protocol
etc`
    },
    // --- MNEMONICS ---
    {
      id: "mnemonics",
      title: "Mnemonics",
      color: "#3498DB", // Light Blue
      content: `# MNEMONICS

## CONSULTATION FRAMEWORK

### GRIPS
- **G** - Greet
- **R** - Rapport
- **I** - Introduce yourself
- **P** - state your Purpose
- **S** - Smile

## PAIN ASSESSMENT

### SOCRATES 
- **S**ite
- **O**nset
- **C**haracter
- **R**adiation
- **A**ssociated symptom
- **T**iming
- **E**xacerbating/Relieving factor
- **S**everity

### ODITPARA
- **O** - Onset
- **D** - Duration
- **I** - Intensity
- **T** - Timing
- **P** - Progression
- **A** - Aggravating factor
- **R** - Relieving factor
- **A** - Association for other symptoms

## HISTORY TAKING

### P3MAFTOSA
- **P** - Past medical history
- **M** - Medication history
- **A** - Allergy
- **F** - Family history
- **T** - Travel history
- **O** - Occupational history
- **S** - Social history
- **A** - Additional history/Anything else

### DESA (Lifestyle)
- **D**iet / Drugs [recreational]
- **E**xercise
- **S**moking
- **A**lcohol

### FLAWS (Red Flags for Cancer)
- **F**ever
- **L**umps and Bumps
- **A**ppetite
- **W**eight loss
- **S**weating [Night sweats]

### ICE
- **I** - Idea
- **C** - Concern
- **E** - Expectations

### JARSS
- **J** - Job
- **A** - Activities
- **R** - Relationship
- **S** - Sleep
- **S** - Sex

## OB-GYN HISTORY

### MSP
- **M** - Menstrual history
- **S** - Sexual history
- **P** - Pills/Contraceptives

## PEDIATRIC DIFFERENTIALS

### PAMGUDU
- **P** - Pneumonia
- **A** - AOM (Acute Otitis Media)
- **M** - Meningitis
- **G** - Gastroenteritis
- **U** - UTI
- **D** - Dehydration
- **U** - URTI

### PBINDS (Pediatric History)
- **P** - Pregnancy
- **B** - Birth history
- **I** - Immunization history
- **N** - Nutrition history
- **D** - Developmental history
- **S** - Social history

## PSYCHIATRY

### FAMISH
- **F** - Family, Friends, finance, forensics
- **A** - Alcohol/smoking, recreational drug
- **M** - Mood, Medications
- **I** - Insight
- **S** - Suicide, Sleep
- **H** - Hallucinations (visual/auditory)

### SCOFF (Eating Disorders)
- **S**ick - Do you make yourself sick after eating?
- **C**ontrol - Do you think that you've lost control over what you eat?
- **O**ne stone - Have you lost more than one stone in the last 3 months? (6.35kg)
- **F**ood - Do you think food dominates your life?
- **F**at - Do you see yourself as being fat even when others think otherwise?

### CAGE (Alcohol Assessment)
- **C**ut down - Have you felt the need to cut down on your drinking?
- **A**nnoyed - Do you feel annoyed by people complaining about your drinking?
- **G**uilty - Do you ever feel guilty about your drinking?
- **E**ye-opener - Do you ever drink an eye-opener in the morning to relieve the shakes?

### SAD PERSONS (Suicide Risk Assessment)
1. **S**ex (male)?
2. **A**ge (<19yrs or >45)?
3. **D**epression or Hopelessness?
4. **P**revious Suicide Attempts?
5. **E**xcessive Alcohol or Drug Use?
6. **R**ational Thinking Loss/Psychosis?
7. **S**eparated, Divorced or Widowed?
8. **O**rganized Plan?
9. **N**o Social Supports?
10. **S**tated Future Attempt to Harm Self?

*Score of ≥6 requires further evaluation*

### AUTISTICS (Autism Assessment)
- **A**gain and again (repetitive behavior/speech)
- **U**nusual (hyper or hypo) reactivity to sensory aspects
- **T**oddlers (during early development)
- **I**nsistence on sameness, inflexible adherence to routines; Interests restricted and abnormal in intensity or focus
- **S**ocioemotional reciprocity deficits
- **T**o relate with others is a difficulty
- **I**mpairment in social, occupational or other functioning
- **C**ommunication (non-verbal) poor for social interaction
- **S**ocial communication below that expected for general development

## DOMESTIC VIOLENCE

### HARK (Domestic Violence)
- **H**umiliate - Does he humiliate you when you're around him?
- **A**fraid - Do you feel afraid when around him?
- **R**ape - Does he sometimes force himself on you even when you don't consent?
- **K**ick - Does he sometimes kick or get physically violent with you?

*Refer to MARAC (Multi-Agency Risk Assessment Conference)*

## FOLLOW-UP & COMMUNICATION

### 5 C's (Follow-up Consultation)
- **C**ondition
- **C**are
- **C**omplication
- **C**ompliance
- **C**oncerns

### SPIKES (Breaking Bad News)
- **S**ettings/Situation
- **P**erception
- **I**nvitation
- **K**nowledge
- **E**motions (EVE Protocol)
- **S**trategy

### 3CONES (Medical Error/Angry Patients)
- **C**ondition
- **C**are
- **C**omplication
- **O**kay shot
- **N**arrate the event
- **E**VE Protocol (for empathy)
- **S**olution or Treatment

### EVE Protocol (Empathy)
- **E**xplore emotions
- **V**alidate Emotions
- **E**mpathize

## SPECIFIC CONDITIONS

### TRANSGENDER HISTORY (OSSD-T)
- **O**rientation - Were you born a male or female? What gender do you now identify as?
- **S**exual history - Are you sexually active? Do you have a stable partner? What's your preferred route of sexual intercourse? Do you practice safe sex?
- **S**upport - Are your family and friends aware that you're transitioning? Are they supportive? Have you heard about support groups like the LGBTQ foundation?
- **D**iscrimination - Are you facing any form of discrimination based on your new sexual identity?
- **T**reatment - Have you officially commenced treatment?

### PRICE (Ankle Swelling Management)
- **P**ainkillers
- **R**est
- **I**ce
- **C**ompress / compression bandage
- **E**levate

### DESPE-rate (Erectile Dysfunction Assessment)
- **D**esire - On a scale of 1 to 5, do you have the desire or urge to have sex?
- **E**rection - On a scale of 1 to 5, are you able to have erection?
- **S**ustain - On a scale of 1 to 5, are you able to sustain an erection?
- **P**enetrate - Are you able to penetrate and sustain it?
- **E**jaculate - Are you able to ejaculate well?

*Additional questions:*
- How long has this been ongoing?
- Anything that makes it better or worse?
- Any early morning erection?
- Any stress at home or work?
- History of STIs?
- Any penile discharge?

## SPECIAL CONSIDERATIONS

### Criteria for Tonsillectomy
- 7 episodes in 1 year
- 5 episodes yearly in 2 years
- 3 episodes yearly in 3 years
- Obstructive symptoms, absence from school, insomnia, daytime sleepiness, failure to thrive, etc.

*Ensure to tell the mother that each episode must be documented at the GP*

### GILLICKS Competence (Contraceptive Assessment)
1. What do you know about these contraceptive pills?
2. What do these pills do?
3. Do you know if these pills sometimes fail?
4. Are you aware these pills may have side effects?
5. Apart from pregnancy, do these pills protect against STIs?

### FRASER Competence
1. Have you spoken to your parents about this? Why haven't you spoken to them?
2. How will you feel today if we do not give me the pills?
3. Are you likely to continue having sex if we don't give you the pill today?
4. Do you know the consequences of having unprotected sex?

### STFAD (Substance Management)
- **S**upport groups
- **T**alking therapy
- **F**riends and family
- **A**ttaining a target; alcoholic anonymous
- **D**iary

### Developmental Milestones Assessment (15-month-old who can't walk)
**Gross motor**
- Can he sit, crawl, stand without support?
- Can the child reach out for an object?

**Fine motor**
- Can he pick an object with 2 hands?
- Can he drink from a cup unaided?

**Speech**
- Does he talk?
- How many words can he say?
- Do the words have meanings?

**Social**
- Does he smile?
- Does he maintain eye contact?
- Does he play with other kids?
- When you cuddle him, does he cuddle back?
- Does he have any particular repetitive behavior?
- Does he have preference for any particular colors or toys?`
    },
    // --- MEDICAL STRUCTURE ---
    {
      id: "medical-structure",
      title: "Medical Structure",
      color: "#4A90E2", // Blue
      content: `# MEDICAL STRUCTURE:\n## GRIPS\n- **Greeting** (examiner & patient)\n  - E(face examiner, point at your tag): Hello! George Edeh, GMC 7123456\n  - P(now face the patient & sit opposite, smile ☺): Hello!\n- **Rapport**\n  - Smile ☺\n  - Ask how they are doing...how the posting is going? etc\n  - Particularly relevant in cases where you are talking to a colleague (medical student or nurse) eg teaching stations\n- **Introduction**\n  - My name is Dr George, one of the doctors in this ...... department (medical dept). I will be the one attending to you today. Can you confirm your name & age pls?\n- **Purpose**\n  - Can you tell me why you are here today?\n  - If something has been done for the person before, start with I understand... and then reference the incident. \n  - Examination or Procedures say ...I have been asked to examine your knee or I have been asked to take your samples for arterial blood gas etc\n- **Smile** ☺\n  - Always wear a smile as you enter a station.\n\n## P3MAFTOSA\n- **Presenting complaints**\n  - Clerk the complaint with either ODIPARA or SOCRATES (for pain)\n  - ODITPARA = Onset, Duration, Intensity, Timing, Progression, Aggravating & Relieving factors, Associations\n  - Note: associations are other symptoms in the same system of pathology.\n  - Apart from this do you have any other complaints? (very impt question)\n  - some will answer you "like what?"\n  - That's where you bring in your DDs\n  - DD's -> pick 2-3 differentials and rule them out with 1-2 questions each.\n- **Past Medical, Medication & Surg Hx**\n  - Have you had something like this in the past?\n  - Is there any medical condition you are being managed for in the past?\n  - Is there any medication you are currently on?\n  - If relevant ask, have you had any surgeries in the past?\n- **Personal Hx**\n  - Menstrual Hx\n  - If its a woman of reproductive age ask about 4 Ps - periods, pregnancy, pills & pap smear\n  - Sexual Hx -> can be here or down\n- **Allergies**\n  - Do you have any allergies?\n- **Family Hx**\n  - Is there anyone in your family with a similar condition?\n  - If relevant, ask a specific family condition eg cancer, htn, DM\n- **Travel Hx**\n  - If relevant explore\n  - Eg in cases of STI patient may have been to Thailand.\n  - Also in cases of TB patient may have been to Kenya (sorry)\n  - Or COVID-19 pandemic\n  - Or DVT/PE -> prolonged flights to Australia\n- **Occupational Hx**\n  - If relevant explore\n  - Eg in cases of Occupational lung dx eg mesothelioma\n- **Sexual Hx** (don't be shy)\n  - If relevant bring it forward & explore\n  - First question: are you in a stable r/ship? If affirmative,\n  - Is your partner male or female?\n  - Are you sexually active?\n  - Whats your preferred route of sexual intercourse?\n  - Do you practise safe sex? ...by safe sex I mean condoms...\n  - Do you use sex toys?\n  - Do you share sex toys?\n  - Explore symptoms of genitourinary problems\n- **Alcohol & Lifestyle Hx**\n  - Signpost🚦here: about your lifestyle, do you smoke? let them answer...do you take alcohol? ...how is your diet?... are you physically active?\n  - If positive alcohol CAGE it - have tried to cutdown? do you get angry when you are criticized about alcohol intake? do you feel guilty about this? do you use alcohol as eye opener first thing in the morning?\n  - If positive smoking - how long? how much?\n  - If positive hx of substance abuse eg heroin - how do you use it? how much? how long? how do you get it? finances?\n  - If injectable - do you share needles? do you know about the needle exchange programme? (educate them about this)\n\n## ICE\n- **Idea** - do you have an idea about what could be going on with you?\n- **Concerns** - at this point do you have any concerns or questions?\n- **Expectations** - what are your expectations for this consultation today?\n  - could be a lifesaver if you are confused on what patient wants from you.\n\n## JARS\n- **Job, Activities, Relationships, Sex life**\n  - you have told me quite alot today... how has these issues affected your job, activities, r/ship and sex life? .\n  - ..let them talk uninterrupted\n\n## EXAMINATION\n- Thank you for answering my questions. At this point I will like to examine you. I will be checking your vitals (or observation chart), I will do a general physical examination and I will specifically take a look at your ......(specific system) \n- I will do this in the presence of a chaperone & I will respect your privacy.\n- Do I have your consent to proceed? ... wait for response\n\n## INVESTIGATIONS\n- I will be doing some baseline investigations. I will do a test for blood counts, I will check how your liver & kidneys are fxning, and do a bedside urine test. Can I go ahead?\n\n## PROVISIONAL DIAGNOSIS\n- From what you have told me and examination findings I suspect......(state diagnosis). Have you heard about that before? They usually say "No"\n\n## EXPLAIN DIAGNOSIS\n- 1 sentence simple definition using ordinary English language\n- Eg. a heart attack means that the blood channels of your heart are clogged by blood clots (which maybe due to the high cholesterol). \n- Would you like to know what we would be doing for you?\n\n## TREATMENT\n- **Inform seniors**\n  - First, I will be discussing your case with my seniors. They will come and have a chat with you incase I missed anything.\n- **Symptomatic Mgt**\n  - I will be starting you on some medications including........\n  - If you have time briefly explain the MOA & side effects\n  - Is that okay by you? or Does this sound like a plan?\n- **Lifestyle advice** if you got any positives.\n- **Referral/Admission/Discharge**\n  - I will be referring you (immediately, urgently or routinely) to see a ...... specialist for ......
  - Eg. cardiologist..... specialist for the heart\n- **Support groups**\n  - Depending on the case.\n- **Leaflets**\n  - I will be giving you a leaflet to learn more about this condition\n  - DON'T GIVE IF PATIENT HAS CANCER or DEPRESSION\n- **Safety-Netting**\n  - If any point you have similar symptoms or ...., ..... and ...... (red flags) don't hesitate to come back to us or go to emergency.\n  - If patient is admitted ask them to ring the bell by pressing the red button at the bedside\n- **Followup**\n  - We will be booking your followup appointment for ..... (eg 1wk)\n\n## OUTRO\n- Thank you for coming in today, see you soon.`
    },
    // --- EXAM SUCCESS STRATEGY ---
    {
      id: "exam-success-strategy",
      title: "Exam Success Strategy",
      color: "#16A085", // Teal
      content: `# EXAM SUCCESS STRATEGY\n\nI have a theory that the easiest way to pass this exam is by setting *relatively attainable targets* within the time you have to prepare. If you have a longer time to prepare you will benefit from a wider study. But if you have a short time to prepare then zone in on the essentials like this course will discuss. Armed with that broad overview you can now work with medical reflexes keeping in mind that you must be a ***safe, smart & nice doctor.***\n\nLet's start with your goals. How many stations do you want to pass? People say 16/16 but realistically you just need 10/16 at least to pass. I want you to shoot for the stars but I also want you to ensure you have landed on the moon first so that you will not be lost in space - floating away.\n\n*So let's analyze the exam requirements and see your chances.*\n\n**__Stations in the PLAB 2 Exam__**\n1. Medicine: majority\n2. O&G\n3. Paediatrics\n4. Angry patient/Medical errors\n5. BBN\n6. Ethics & Counselling\n7. Psychiatry\n8. Pre-op/ Post-op Assessment\n9. Discharge station\n10. Follow-up station\n11. Teaching station\n12. Prescription\n13. Examination/Procedure\n14. Simman/Simwoman\n\n- Medicine is said to take about 60% of the exam so if you hold it tightly you already have something strong. Moreover, there's no where you can't practice with good background medical knowledge.\n- Let's count others you can grab easily 1 by 1\n  - Teaching stations - its so straightforward. Many of you have taught a medical student, a class mate or a junior colleague in the past. This time around you just add structure to it.\n  - Followup stations - I like this type of stations because patient already has results or you already know the diagnosis. One problem down. Its just to interact with the patient tactfully and address their concerns.\n  - Pediatrics - just like adult medicine cases. No way you wouldn't see at least 1 in the exam.\n  - OBGYN - you will meet at least 1 case from this. And its either its a pregnant woman or a non-pregnant woman.\n  - Examination stations - there's no way you won't see at least 1 examination station. It has a structure and what they are looking for is just simple.\n  - Procedure stations - just like examination stations its easy to master. Put yourself in the shoes of a house officer and add the skills in your bank.\n  - SIMMAN/SIMWOMAN stations - this is one of the stations people unnecessarily overhype. But its a practical station, easy like ABCDE.\n  - Definitely they will give you 1 or more angry patients or medical errors or breaking bad news. These require some level of tact & communication skills. Gradually you will get a hang of those skills.\n  - Prescription is the only station where you don't need to interact with a patient. Just write what they tell you to write into the prescription sheet they give you. There are some technicalities and catch to be wary of here due to medication interactions. But you will get a hang of it with practice. Visit the prescription section. We made it easy.\n\nSo you see, 1 by 1 we have given ourselves the best chance of success by starting from *the most-required to the easiest and then the toughest* if time/situation permits. This is my success strategy for PLAB2. You can copy and modify as you wish.`
    },
    // --- PSYCHIATRY STRUCTURE ---
    {
      id: "psychiatry-structure",
      title: "Psychiatry Structure",
      color: "#9B59B6", // Purple
      content: `# PSYCHIATRY STRUCTURE:\nAdd and subtract a few things\n\n## GRIPS\n- Note that you are usually talking to a psych patient who may be going through depression, or mania or schizophrenia. Patient may be suicidal or aggressive or uncooperative or hallucinating or talking abnormally.\n- so you need to be careful & show lots of sensitivity.\n- acknowledge the behaviour of the patient to create rapport. Eg he may be standing and pacing about. he may be looking around and uncomfortable. may be avoiding eye contact.\n- Say something like ...I can see that you are looking around... is there something going on?\n\nI use **PMAFAMISH**\n\n## PMAF\n- Don't do the entire PMAFTOSA, just do only PMAF!\n- **Presenting Complaints**\n- **+ PMSHx** (if relevant) + **Medications** + **Allergy** + **Fam Hx** (of psych illness)\n\n## FAMISH\n- this will help you characterize the problem very well.\n- **Family, Friends, Forensics & Finances**\n  - ask about the people in their lives & support at home?\n  - have you thought about harming anyone? do you have any weapons? collect the weapons and keep aside\n  - how have you been coping with your finances?\n- **Alcohol & Other substances**\n  - use of recreational drugs\n- **Mood, Memory**\n  - how is your mood?\n  - any memory problems? -> Dementia\n- **Insight**\n  - do you have an idea about what could be going on? (form of ICEing)\n  - do you think this is a problem?\n- **Suicide**\n  - I can see how overwhelming these issues are to you...have you ever thought about ending it all?\n- **Hallucinations, Delusions & Thought Disorder**\n  - Do you see or hear things that other people do not agree with?\n  - Do you have any beliefs that other people do not agree with?\n  - Do you feel like thoughts are being added or removed from your head?\n  - Do you feel that other people can read your mind?\n\n## DIAGNOSIS\n- Don't tell them their diagnosis straight ahead. Try to come from an angle...its all about word framing to convey sensitivity.\n- Eg you know you told me that ....you are hearing people talking to you...in people that experience something similar, we do say they have a condition called "psychotic disorder". that may not be your case but we just have to be on the safe side.\n- I will like to admit you to protect you from these people.\n\nEvery other thing applies.`
    },
    // --- PEDIATRICS STRUCTURE ---
    {
      id: "pediatrics-structure",
      title: "Pediatrics Structure",
      color: "#2ECC71", // Green
      content: `# PEDIATRICS STRUCTURE:\nAdd and subtract a few things\n\n## GRIPS\n- Note that you are usually talking to the patient relative ie mother or father.\n- so you need to learn 2 names\n- show lots of sensitivity - its a child!🧒\n\nI use **3 Ps** for Pediatrics\n\n## PMAF\n- Don't do the entire PMAFTOSA, just do only PMAF!\n- **PMSHx + Medications + Allergy + Fam Hx**\n\n## PAMGUDU\n- Its should come in when you are seeking DDs for any childhood condition especially ones with fever\n- **Pneumonia** - cough, breathlessness\n- **Acute Otitis Media** - tugging at ears, ear discharge\n- **Meningitis** - neck stiffness, shying away from light\n- **Gastroenteritis** - diarrhoea, tummy colics, vomiting\n- **UTI** - crying while peeing, dry napkins,\n- **Dehydration**- crying without tears, dry napkins, skin tugor\n- **URTI** - runny nose, red eyes etc\n\n## PBINDS\n- Very important for alot of pedo cases\n- **P**\n- **B**\n- **Immunization** - is he up-to-date with jabs?\n- **Nutrition** - is he eating well?\n- **Developments** - are you happy with his red book?\n\nEvery other thing applies.`
    },
    // --- OBGYN STRUCTURE ---
    {
      id: "obgyn-structure",
      title: "OBGYN Structure",
      color: "#E74C3C", // Red
      content: `# OBGYN STRUCTURE:\nAdd and subtract a few things\n\n## GRIPS\n- Note that you are usually talking to a woman\n- show lots of sensitivity especially if its a pregnant lady.\n- Confirm that the pregnancy is desired, then congratulate her.\n\nBefore PMAFTOSA, I use **2 Ps** for OBGYN\n\n## PRESENT\n- If pregnant, do present OBS hx\n- **Patient's hx**\n  - is preg desired?\n  - is preg planned?\n  - how long ago?\n  - LMP?\n  - have you booked? (if booking is not why they came)\n  - did you do booking investigations? normal?\n  - any complaints?\n- **Fetus hx**\n  - fetal kicks?\n  - past USS - normal?\n\n## PAST\n- **Past Obs**\n  - basically to determine G? P? +? A?\n  - how many pregnancies?\n  - to term or miscarriage?\n  - how many living children?\n  - mode of birth? CS or vaginal\n  - any complications?\n  - any HTN DM etc\n- **Past Gynae**\n  - 3 P's discussed earlier\n  - **Periods, Pills, Pap smear**\n  - how is your period? (open-ended)\n  - are they regular?\n  - how many days?\n  - how much pads do you change?\n  - are you aware of contraceptive pills?\n  - do you use any?\n  - are you aware of pap smear?\n  - have you do it before?\n  - when was the last session?\n  - what was the finding?\n\nEvery other thing applies.`
    },
    // --- FOLLOW-UP STATION STRUCTURE ---
    {
      id: "follow-up-station",
      title: "Follow-up Station",
      color: "#F39C12", // Orange
      content: `# FOLLOW-UP STATION STRUCTURE:\n\nA followup station captures the consultation of a patient that is not a first timer. It may be a result sharing station or a patient referred from somewhere else. Usually you already know the diagnosis so there's no need to do the detailed hx outlined earlier.\n\nYour major challenge is the time factor. So you need to be particularly careful about followup stations. If you do everything from A - Z you may not be able to finish the station. Here's the flow pattern I suggest:\n\n## GRIPS\n- Since the patient has been here before, bring that up in your purpose\n- Eg in result sharing you can say - I understand you were here before...is that correct?\n- when they affirm... you say ...I have your results here with me...but before we continue I will like to ask you a few questions so we can be on the same page.\n- when they affirm... you dive in straight to **5C's**\n\n## 5 C's\n- **Complaint**\n  - what brought you to the hospital initially?\n  - ask associations of the results/diagnosis...\n- **Condition**\n  - what did they tell you is the problem?\n- **Care**\n  - what have been done for you so far?\n- **Complications**\n  - any new symptoms?\n- **Concerns**\n  - is there something specifically bordering you at this time?\n\n## PMAFTOSA\n- very short and straightforward. ask only relevant areas.\n\n## ICE\n- **Idea (only!)** - do you have any idea about what could be contained in these results?\n\n## JARS\n- same as before\n\n## EXAMINATION\n- same as before\n\n## DIAGNOSIS\n- like I said earlier, I have your results here...show them...\n- you have this and this\n- based on this a diagnosis of -----has been made. have you heard about that before?\n- other parts ... same as before\n\n## MANAGEMENT\n- same as before`
    },
    // --- TEACHING STATIONS ---
    {
      id: "teaching-stations",
      title: "Teaching Stations",
      color: "#3498DB", // Light Blue
      content: `# TEACHING STATIONS\n\nUsually a nurse, a medical student or a junior colleague is coming to you to teach them what you know. Regardless of the case scenario, once you fit this formular in you will find yourself talking. Let's go...\n\n## GRIPS\n- GRIPS is almost same except for rapport.\n- **Rapport is very important**\n  - Greet-introduce yourself by your first name without adding your title.\n  - Call them by their first name\n  - Eg. I'm George. I work in the department of medicine. You are Josh right?\n  - Smile, shake hands if possible.\n  - Ask how they are doing?\n  - Which posting are you doing now? How is your posting going?\n- **For the purpose show that you are aware.**\n  - Eg. I understand you want to learn about ECG is that correct?\n  - Once they affirm, ask...\n  - Is there a reason why? ...they will tell you...\n- **Compliments!** - I commend your knowledge-seeking behaviour...\n- **What do you know about this ?**\n  - They tell you...you commend again\n  - I see you have some knowledge about this I will be filling in the gaps for you okay?\n- **This is my outline**\n  - Outline is usually\n    - Introduction\n    - Body - varies case by case\n    - Test their understanding\n    - Conclusion\n- **Chunking & checking**\n  - Always stop in between to make sure they understand and are following.\n  - Ask them to repeat what you said.\n  - Ask them to redo what you did.\n- **Ask for questions & concerns**\n- **Give leaflets and links** to NHS website for the topic\n- **Outro**\n  - Thank them for coming in\n  - Offer future help (safety-netting)\n  - Eg. Thank you for coming in today to seek knowledge. Keep it up. If you have any confusion in the future I'm happy to help. Also anytime I come across this type of case I will call you to come and witness it. Alright? Cheers!`
    },
    // --- EXAMINATION & PROCEDURE STRUCTURE ---
    {
      id: "examination-procedure",
      title: "Examination & Procedure",
      color: "#1ABC9C", // Turquoise
      content: `# EXAMINATION & PROCEDURE STRUCTURE\n\nBasically the same thing as the above just few subtle differences\n\n## PROCEDURE:\n### PRE-PROCEDURE\n- **GRIPS** -> state your purpose - My name is Dr George one of the doctors in this med dept. I am here to perform ___ procedure. \n- **Consent** -> I will do this in the presence of a chaperone & I will respect your privacy...do I have your consent to proceed?\n  - Note - chaperone & privacy is not in cases like ABG, blood culture, venepunction, IV line etc\n- If affirmative, say ...before that I will like to ask you some questions just to make sure everything is fine. okay?\n- **History**\n  - whats your arm preference?\n  - whats your vein preference\n  - any needle phobia? if yes suggest local pain numbing\n  - do you have any bleeding or clotting problems?\n  - are you on any med including blood thinners?\n  - any allergies?\n- **Examination**\n  - Verbalize a quick general examination (case by case basis)\n- **Positioning & Exposure**\n  - please sit comfortably and expose your arms...\n- **Explain procedure**\n  - I will be passing a sterile needle through your vein, you will feel a sharp scratch. I will be gentle but if at any point you feel uncomfortable ask me to stop. okay?\n- **Gather materials** -> I will be getting the materials I need (case by case basis)\n\n### PROCEDURE\n- Do the procedure (see procedure & examination section of this course for details)\n\n### POST-PROCEDURE\n- Check that they are still okay.\n- Just put a light pressure at this site for me okay?\n- **Safety-netting**\n  - If at any point you notice bleeding, redness or swelling at the site please call our attention...\n- **Outro**\n  - Thanks for coming in. Cheers!`
    },
    // --- OPHTHALMOLOGY STRUCTURE ---
    {
      id: "ophthalmology",
      title: "Ophthalmology",
      color: "#E67E22", // Dark Orange
      content: `# OPHTHALMOLOGY STRUCTURE\n\nHX is basically the same thing as the main medical structure you just need to keep it short/sweet because of time. Remember to ask this important question after the JARS:\n\n- How did you get to the hospital?\n- If they drove...ask...\n- Have you ever had an RTA/challenges on the road due to visual problems?\n- Then immediately chip in DVLA (so that you don't forget it)\n- **You will need to STOP DRIVING and inform DVLA, This is very important to protect you and other road users. Is that alright?**\n\n## EXAMINATION\n\nMost eye stations are COMBINED STATIONS so you need to do the real examination\n- At this point I will like to examine you...I will be checking your vitals, do a general physical examination and I will specially look at your eye. I will be looking and touching. I will also be using a special instrument with light source called an ophthalmoscope to look at the back of your eyes. I will do this in the presence of a chaperone & I will respect your privacy. **DO I HAVE YOUR CONSENT TO PROCEED?**\n- **DO ->**\n- **INSPECTION**\n  - inspect for redness, swelling, tearing, escoriations\n- **VA** -> how far you can see\n- **VF** -> how wide you can see\n- **Fundoscopy** -> looking at the back of the eyes\n  - Its RRR or LLL ie right hand->your right eye -> patient's right eye\n  - come from the side\n  - keep your head horizontally (not vertically)\n  - the examiner will be watching you keenly so be careful\n  - check the eye section of this course for spot diagnosis fundoscopy pictures\n- **PALPATION**\n  - Tonometry -> reserved for specialists\n\n## MGT for most ophtha stations will involve referral to ophthalmologist\n\n- This condition you have is eye threatening so I will be referring you (IMMEDIATELY) to the eye specialist known as ophthalmologist to take further look at your eyes and manage you properly.`
    },
    // --- ENT STRUCTURE ---
    {
      id: "ent-structure",
      title: "ENT Structure",
      color: "#8E44AD", // Dark Purple
      content: `# ENT STRUCTURE\n\nHX is basically the same thing as the main medical structure you just need to keep it short/sweet because of time. Remember to ask this important question in the LIFESTYLE area:\n\n- **Barotrauma** -> did anything or anyone hit your ear?\n  - If affirmative...explore further...\n- **Acoustic trauma** -> did you get exposed to loud sounds previously?\n  - you might find the patient is a past DJ. Had to stop due to the hearing problems\n\n**JARS very well**
- People living with hearing disturbances can feel the effects on their job, daily activities, r/ship and maybe even sex life. So grab your marks from here.\n\n## EXAMINATION\n\nMost ear/nose stations are COMBINED STATIONS so you need to do the real examination\n- At this point I will like to examine you...I will be checking your vitals, do a general physical examination and I will specially look at your ears. I will be looking and touching. I will also be using a special instrument with light source called an otoscope to look into your ears. I will do this in the presence of a chaperone & I will respect your privacy. **DO I HAVE YOUR CONSENT TO PROCEED?**\n- **DO ->**\n- **INSPECTION**\n  - inspect for redness, swelling, discharges, etc\n- **PALPATION**\n  - Tragus tenderness\n  - Lymph nodes (if relevant)\n- **Tuning fork tests** -> Weber's & Rinne's test\n  - explained in the examination section of this course\n- **Otoscopy**\n  - Start with good ear, then bad ear\n  - change the cap of otoscope in-btw (or mention it)\n- **Dix-Hallpike Manoeuvre**\n  - For dizziness stations\n  - positive in BPPV\n\n## MGT for most ENT stations will involve referral to ENT specialist\n\n- I will be referring you to the ENT specialist to take further look at your ears and manage you properly`
    },
    // --- PREOP STRUCTURE ---
    {
      id: "preop-structure",
      title: "Preop Structure",
      color: "#34495E", // Dark Gray-Blue
      content: `# PREOP STRUCTURE\n\nThis station captures what you will do for a patient that is just about going for surgery\n\n**RULE No 1** -> attack it like a followup station (coz its actually a followup station)\n\n- So use **5C's** -> complaint, condition, care, complications.\n- move concerns lower down (coz there are alot)\n- Add a quick review of systems head to toe\n  - headache, fever, cough, chest pain, tummy pain, urinary problems, bleeding problems\n\n- Do **PMAFTOSA**\n  - Make sure you ask past surgical, past hosp admissions, past transfusions, use of blood thinners, allergies to anaesthesia etc\n  - Any dentures?\n  - Any neck pain/swelling?\n  - 4Ps for female patients\n  - Social hx-> do you have family/friends support? who will stay with you after the surgery?\n  - if nobody, offer to be there them.\n\n- **QUICK EXAMINATION**\n  - Go for vitals and GPE\n  - Check BMI\n  - Check neck and mouth\n\n- **INVESTIGATIONS**\n  - Routine tests - CBC, URINE DIPSTICK, GXMATCH, CXR, ECG\n\n**RULE No 2** -> CONCERNS! CONCERNS!! CONCERNS!!!\n\n- When can I resume driving?\n  - as soon as you can apply emergency brakes\n- When can I have sex?\n  - 6-8wks\n- My friend had same surgery with infections\n  - We will maintain all universal hygiene protocols to prevent transmission of infections \n  - Where indicated, we will give you prophylactic antibiotics\n- Will I bleed?\n  - We have very good surgeons who will meticulously perform the operation\n  - But if bleeding does occur we will manage appropriately\n- **IF PATIENT IS A JEHOVAH's WITNESS**, give them alternative options:\n  - Blood products without red blood cells\n  - Injection of Exogenous Hematopoetin\n  - Plasma expanders (most times they go for this)\n- My friend had clotting challenges...\n  - We will give you pressure socks\n  - Our mobility specialist will get you walking within 3 days after operations\n\n**RULE No. 3** -> Safety-Netting\n\n- If at any point you feel dizzy, tummy pain, breathlessness or calf pain please call our attention`
    },
    // --- POST-OP STRUCTURE ---
    {
      id: "postop-structure",
      title: "Postop Structure",
      color: "#27AE60", // Dark Green
      content: `# POST-OP STRUCTURE\n\nThis station captures what you will do for a patient just after surgery from the theater until they get home.\n\n**RULE No 1** -> attack it like a followup station (coz its actually a followup station)\n\n- So use **5C's** -> complaint, condition, care, complications.\n  - emphasize care -> how do you see our services? hope the staff are treating you well?\n- move concerns lower down (coz there are alot)\n\n- Do **PMAFTOSA**\n  - Make sure you ask occupation hx -> heavy lifting can cause postop wound healing problems\n  - Social hx-> do you have family/friends support? who do you live with? \n  - how is the house? kind of house? is there staircase? is there shower downstairs? whats the height of toilet seat?\n  - Note - in mgt you must mention that occupational therapist will go to their house to ensure everything is perfect before they are discharged\n\n- **QUICK EXAMINATION**\n  - Go for vitals and GPE\n\n**RULE No 2** -> CONCERNS! CONCERNS!! CONCERNS!!!\n\n- **MGT: What will happen after the surgery** (still explain the below even if they didn't ask)\n  - Surgical theater\n  - to Recover room for monitoring\n  - to Ward (surgical)\n  - fluids -> graded oral sips asap\n  - pain mgt\n  - early mobility by mobility specialist/ socks / pneumatic compression\n  - blood thinners\n  - occupational health visits home to check everything \n- **Discharge Home**\n  - don't climb stairs\n  - don't turn suddenly\n  - don't cross legs\n  - take little steps\n  - don't sit on low seats\n  - change toilet seats to high ones\n  - showers instead of bath + rails on showers to prevent falls\n- When can I resume driving?\n  - as soon as you can apply emergency brakes\n- When can I have sex?\n  - 6-8wks\n- My friend had same surgery with infections\n  - We will maintain all universal hygiene protocols to prevent transmission of infections \n  - Where indicated, we will give you prophylactic antibiotics\n- Will I bleed?\n  - We have very good surgeons who will meticulously perform the operation\n  - But if bleeding does occur we will manage appropriately\n- **IF PATIENT IS A JEHOVAH's WITNESS**, give them alternative options:\n  - Blood products without red blood cells\n  - Injection of Exogenous Hematopoetin\n  - Plasma expanders (most times they go for this)\n- My friend had clotting challenges...\n  - We will give you pressure socks\n  - Our mobility specialist will get you walking within 3 days after operations\n\n**RULE No. 3** -> Safety-Netting\n\n- If at any point you feel dizzy, tummy pain, breathlessness or calf pain please call our attention or ring the bell on your bedside`
    },
    // --- INTER-PERSONAL SKILLS ---
    {
      id: "interpersonal-skills",
      title: "Inter-Personal Skills (IPS)",
      color: "#F39C12", // Orange
      content: `# INTER-PERSONAL SKILLS (IPS)

*Inter-personal skills refers to how you communicate with a patient. This includes verbal and non-verbal communication (body language).*

Let me tell you something - you can know all the medicine in the world, but if you can't effectively communicate with your patients, you're only half a doctor. In PLAB2, your IPS is what sets you apart from others. I've seen brilliant doctors fail simply because they couldn't connect with their patients. Don't be that doctor.

## VERBAL IPS

### INTRODUCTION & BUILDING RAPPORT

This is your first impression - make it count!

When I entered my first station, I was so nervous I almost forgot to introduce myself. Don't make that mistake. A simple "Hello, I'm Dr. George" with a smile works wonders. 

Real example: I once had a case with an elderly patient who seemed withdrawn. Instead of diving straight into questions, I asked how her journey to the hospital was. Her face lit up as she told me about her grandson who drove her. That 30-second investment built enough rapport for a smooth consultation.

**Pro tips:**
- Sit at the same level as your patient, not towering over them
- Address them by their preferred name (ask if unsure)
- Acknowledge their emotions: "You seem worried today..."
- Show genuine interest in their responses

### DATA GATHERING

This isn't an interrogation - it's a conversation.

I remember a psychiatric case where a patient had depression but wasn't forthcoming. Instead of firing questions, I used open-ended questions: "Can you tell me more about how you've been feeling lately?" Then I nodded encouragingly, maintained eye contact, and used gentle prompts like "I see" or "go on" to keep them talking.

**Practical examples:**
- Instead of "Do you have chest pain?" (closed), try "Tell me about any discomfort you've experienced" (open)
- When exploring sensitive topics (sexual history, mental health), signpost: "I'd like to ask some questions about your personal life that will help me understand your situation better. Is that okay?"
- After a patient speaks, briefly summarize to show you're listening: "So you've had this cough for three weeks, and it's worse at night..."
- Use silence effectively - some patients need time to gather thoughts

In a diabetes follow-up station, my patient mentioned "feeling a bit down" in passing. Instead of moving on with my checklist, I picked up on this cue: "You mentioned feeling down. Could you tell me more about that?" This revealed undiagnosed depression that was affecting medication compliance.

### EXAMINATION

Communication doesn't stop during physical examination.

I had a station with a nervous teenage girl needing an abdominal exam. I explained each step before doing it: "Now I'm going to gently press on your tummy to check for any tenderness. You might feel my hands here... and now here. Please tell me if anything feels uncomfortable."

**Examination IPS essentials:**
- Always explain what you're about to do before doing it
- Maintain dignity - offer appropriate covering, chaperones
- Keep talking during the exam: "I'm now checking your reflexes, this shouldn't hurt"
- Give positive reinforcement: "You're doing great, just a few more checks"
- Ask for feedback: "Are you comfortable with me continuing?"

### DIAGNOSIS

Breaking news (good or bad) requires finesse.

In a hypothyroidism case, instead of saying "Your thyroid is underactive," I explained: "Your thyroid gland, which is here in your neck, isn't producing enough hormones. This explains your tiredness, weight gain, and feeling cold all the time."

**Effective diagnosis communication:**
- Assess what they already know: "What's your understanding of what might be happening?"
- Use simple language, avoid jargon
- Check understanding: "To make sure I've explained clearly, could you tell me what you understand about your condition?"
- Acknowledge emotions: "I can see this news is upsetting for you"
- Be honest but hopeful when appropriate

In a hypertension station, my patient seemed dismissive of the diagnosis. Instead of lecturing him, I asked: "What concerns you most about having high blood pressure?" He revealed he'd seen his father suffer stroke complications from hypertension. Addressing this specific fear made him more receptive to treatment.

### MANAGEMENT

This is a partnership, not a dictatorship.

I had a COPD patient who smoked. Instead of saying "You need to quit smoking," I said "What are your thoughts about your smoking in relation to your breathing problems?" When she showed interest in quitting, I asked, "What has prevented you from quitting before?" This revealed previous failed attempts and allowed me to tailor support appropriately.

**Management IPS techniques:**
- Involve the patient: "There are several options we could consider. Would you like me to go through them?"
- Check preferences: "Some people prefer tablets, others injections. Do you have a preference?"
- Explain rationale: "This medication will help because..."
- Address practical concerns: "This needs to be taken with food. Is that manageable with your work schedule?"
- Confirm understanding and agreement

### DIFFICULT SITUATIONS

Every doctor faces challenging interactions - your IPS arsenal helps here.

I once had an angry patient whose surgery had been rescheduled twice. Instead of getting defensive, I acknowledged his frustration: "I can see you're upset about this, and honestly, I completely understand why. It's extremely frustrating to prepare yourself mentally for surgery only to have it postponed." This simple validation immediately diffused his anger.

**Handling difficult situations:**
- Acknowledge emotions first before problem-solving
- Don't take hostility personally
- Use "we" language: "How can we work together to address this?"
- Set boundaries kindly but firmly: "I understand you're frustrated, but I need to ask these questions to help you properly"
- With interruptions: "I'd like to hear everything you're concerned about. Could I just finish this question first?"

### CULTURAL SENSITIVITY

The NHS serves diverse communities - your IPS must be adaptable.

During a female health station, I had a patient from a conservative background. I noticed her discomfort and offered: "Would you prefer to see a female doctor for this examination? I can arrange that if you'd be more comfortable." She was visibly relieved and thanked me for understanding.

**Cultural IPS considerations:**
- Be aware of different communication styles and preferences
- Ask about specific cultural or religious needs
- Avoid assumptions based on appearance or name
- Respect personal space preferences
- Use professional interpreters when language barriers exist (not family members for sensitive issues)

### SAFETY NETTING & CLOSURE

How you end the consultation is as important as how you begin it.

I always finish with clear instructions about what to watch for and when to seek help: "If your headache gets suddenly worse, if you start vomiting, or if your vision changes, please go straight to A&E." Then I check understanding: "Just so I'm sure you know what to look out for, what symptoms would make you come back urgently?"

**Effective closure:**
- Summarize key points
- Provide specific follow-up instructions
- Leave the door open: "If you think of any questions later, please don't hesitate to contact us"
- End on a positive note when possible: "I'm really impressed with how you've managed so far"

Remember, great IPS isn't just about passing PLAB2 - it's about becoming the kind of doctor patients trust and colleagues respect. These skills will serve you throughout your entire career.`
    },
    // --- NON-VERBAL CUES ---
    {
      id: "non-verbal-cues",
      title: "Non-verbal Cues",
      color: "#8E44AD", // Purple
      content: `# NON-VERBAL CUES

*Effective communication in healthcare extends beyond spoken words. Understanding non-verbal cues is crucial for healthcare professionals working within the National Health Service (NHS). Non-verbal cues can provide valuable insights into a patient's emotions, concerns, and overall well-being.*

## 1. Body Language

Observing a patient's body language can reveal a wealth of information. This is why you must always maintain eye contact with your patient. For example:

- **Case Example 1:** A patient crossing arms or avoiding eye contact may indicate discomfort or anxiety. Eg cases of *domestic violence, sexual abuse, sex-trafficking* etc.
- **Case Example 2:** *Agitation, restlessness, or fidgeting* might suggest **pain** or unease. Eg a patient in severe pain maybe restless. Please acknowledge this in your IPS.
- **Case Example 3:** Another common scenario is a patient with *psychotic disorder*. He/she will be obviously *DISTRACTED, looking at the door, standing and pacing as if expecting someone*. Acknowledge this in your IPS and use it as opener.

## 2. Facial Expressions

The face is a powerful indicator of emotions. Pay attention to:

- **Case Example 1:** A patient's *facial grimace* might signal pain or discomfort. Eg chest pain as in *acute coronary syndrome*
- **Case Example 2:** A sudden *change in expression, such as a furrowed brow*, could indicate confusion or distress. Eg. Simman deteriorating.
- **Case Example 3:** A *depressed* patient will look *low and sad with a flat affect.*

## 3. Tone of Voice

Even when words are limited, tone conveys emotions. Consider:

- **Case Example 1:** A *hesitant or shaky voice may indicate nervousness or fear*. Eg a patient hiding something personal like same *sex attraction, domestic violence, erectile dysfunction or rashes in private part* etc
- **Case Example 2:** A *sudden change in tone* could signify a shift in emotional state or pain. This is typical when you are **breaking a bad news**. Please *acknowledge this emotion, validate it and show empathy.* You could say, *"I can see that you are obviously disturbed by what I just said. Anyone in your shoes would feel the same way. I am so sorry that you are going through this however we are here to support you in every way possible." **Remember, your tone of voice must match theirs. So if they are low, yours should be low as well.***

## 4. Proximity and Touch

Physical proximity and touch should be approached with sensitivity:

- **Case Example 1:** Some patients may appreciate a reassuring touch, while others may find it intrusive. Eg a handshake might be good before a consultation depending on the case. Maybe hold off on it in this PLAB2 exam.
- **Case Example 2:** Standing too close might make a patient feel uncomfortable; maintain appropriate personal space. ***Always take consent before examining or touching a patient.***
- **Case Example 3:** A crying patient is in an strong emotional state. DON'T TALK FOR 20SECS. PUSH THE TISSUE PAPER TOWARDS HER. This is the time for topnotch IPS. Tell her to take her time but that you are here to support her when she's ready to continue.

---

**Body language can also be about you as well because it is part of your IPS. Here are some general guides:**

1. Sit face-to-face with patient
2. Maintain a confident posture (situp)
3. Smile when coming into a station (GRIPS)
4. Maintain eye contact so you can observe body language and facial expressions. Eg patient can say "no" by shaking her head or say "yes" by nodding.
5. Look away only briefly. Eg when looking at the prompt, or checking BNF medicine book etc.
6. Let your facial expression match the situation eg patient just gave birth, congratulate her. In BBN, look sad.
7. Let your tone of voice match the situation eg low tone in sensitive cases (BBN, LGBTQ, DV, etc).
8. If a patient is doing well compliment him/her. Eg. if he has been eating well, physically active etc.
9. Also use non-verbal cues like pointing at the examination table when you are telling a patient that you want to examine.`
    },
    // --- REFERRAL TYPES ---
    {
      id: "referral-types",
      title: "Referral Types",
      color: "#2980B9", // Blue
      content: `# REFERRAL TYPES

---

### 1. Emergency Referral:
**Duration:** Immediate, often same-day (within 24 hours).
- Severe trauma requiring immediate attention.
- Acute myocardial infarction (heart attack).
- Stroke with sudden onset of symptoms.
- Anaphylactic reactions.
- Respiratory distress in asthma exacerbation.
- Acute Eye conditions eg AACG (Glaucoma), Anterior Uveitis etc

### 2. Very Urgent Referral:
**Duration:** Within 48-72 hours.
- Suspected cancer cases for expedited diagnostic assessments.
- Acute exacerbation of chronic conditions like COPD.
- Rapidly progressing neurological symptoms.
- Severe mental health crises.
- High-risk obstetric situations.

### 3. Urgent Referral:
**Duration:** Typically within 2 weeks.
- Suspected cancer cases where urgency is moderate.
- Orthopedic conditions requiring prompt assessment.
- Dermatological issues needing timely attention.
- Persistent gastrointestinal symptoms.
- Cardiovascular assessments for non-emergent cases.

### 4. Routine Referral:
**Duration:** Non-urgent cases with a longer waiting time (varies).
- Non-acute musculoskeletal consultations.
- Routine follow-ups for stable chronic conditions.
- Ophthalmology assessments for non-urgent issues eg refractory errors.
- Non-urgent gynecological consultations.
- Routine dermatological screenings.

### 5. Others (Community, Direct Access, Inter-Professional, Maternity, etc.):
- **Community Referral:** Rehabilitation services, community physiotherapy, mental health support.
- **Direct Access or Self-Referral:** Family planning, sexual health services, some mental health services.
- **Inter-Professional Referral:** GP to specialist consultations, collaborative care planning.
- **Maternity Referral:** Antenatal care, midwife services, high-risk pregnancy management.
- **Specialist Clinics:** Cardiology clinics, rheumatology clinics, diabetes clinics.`
    },
    // --- SPECIAL REFERRAL CLINICS ---
    {
      id: "special-referral-clinics",
      title: "Special Referral Clinics",
      color: "#E74C3C", // Red
      content: `# SPECIAL REFERRAL CLINICS

---

*In NHS, when you see a patient the next line of management is to either admit or refer depending on the case. Below are some special clinics where you will refer to.*

## **SPECIALTY MEDICINE CLINICS**

**_Nephrology Clinic_**

**_Endocrinology Clinic_**

Neurology Clinic

Cardiology Clinic

Gastroenterology Clinic

Genito-Urinary Medicine (GUM) Clinic

Dermatology Clinic

Paediatrics

## **SPECIALTY SURGERY CLINICS**

Vascular surgeons - PAD, acute limb ischemia, abdominal aortic aneurysm

Neurosurgeons - emergency referral in Cauda equina syndrome

## **SUBSPECIALTY CLINICS**

ENT

Ophthalmology

## **Others**

**_First Fit Clinic_**

**_Sexual Health Clinic_**

Fertility Clinic

Rapid Chest Assessment Clinic

DVLA - inform DVLA and stop driving in eye/ear problems, strokes, Alzheimers etc

## **Mental Health Clinics**

**_Psychiatry Clinic_**

**_Substance Abuse Clinic_**

**_Cigarette Smoking Cessation Clinic_**

## **Social Health Issues**

**_Social services_**

MARAC

SARC

LICB

Local Authorities

Local Job Services

## **LGBT-RELATED CLINICS**

GIDS

GDC

Counsellor

## **Others**

**_For those on long-term anticoagulants_** (eg warfarin, rivaroxaban, apixaban). This will alert doctors managing their care.

**_Crisis Card_** - Hand a crisis card to patients with depression at risk of suicide. Usually there is a phone number they can call right there to reach help directly.

**_No Blood Transfusion Card_** - usually carried by Jehovah Witnesses. This alerts helpers that there will be no blood transfusion

**_Do Not Resuscitate Card_** - usually carried by elderly people who have chronic conditions and have stated that they do not want to be resuscitated if they stop breathing or their heart stops beating. This does not mean they cannot receive other care.`
    },
    // --- ANGRY PATIENT ---
    {
      id: "angry-patient",
      title: "Angry Patient",
      color: "#C0392B", // Pomegranate Red
      content: `# ANGRY PATIENT\n\nhttps://www.youtube.com/watch?v=Y1PbCGZojf0\n\n## Introduction:\n\n*One of the critical skills tested in this station is your ability to handle challenging patient interactions effectively. In this lecture, we will explore strategies to navigate through such scenarios, ensuring optimal patient care and a successful outcome for the examination.*\n\n## Understanding the Situation:\n\n*In this PLAB 2 station, you may encounter an angry patient expressing dissatisfaction, frustration, or even aggression. It\'s essential to recognize the underlying reasons for the patient\'s anger, which can range from dissatisfaction with medical care, communication issues, to personal stressors.*\n\n## **Mnemonics: CONES**\n\n**C** - Complaint, Complication, Care\n\n**O** - Opening Shot\n\n**N** - Narration\n\n**E** - Empathy/Emotions\n\n**S** - Strategy/Solution\n\n## **Key Strategies:**\n\n1. **Remain Calm and Composed:**\n   - Take a deep breath and maintain a calm demeanor.\n   - Remember that the patient\'s anger is not directed personally at you but may stem from broader concerns or misunderstandings.\n\n2. **Active Listening:**\n   - Allow the patient to express their feelings without interruption.\n   - Use empathetic phrases such as \"I understand\" and \"I hear what you\'re saying\" to convey that their concerns are being acknowledged.\n\n3. **Empathy and Validation:**\n   - Demonstrate empathy by acknowledging the patient\'s emotions.\n   - Validate their concerns, even if you don\'t necessarily agree with them. This shows respect for their perspective.\n\n4. **Non-Verbal Communication:**\n   - Maintain appropriate eye contact and body language.\n   - Ensure your tone of voice is calm and reassuring, avoiding any confrontational gestures.\n\n5. **Clarify and Seek Information:**\n   - Ask open-ended questions to encourage the patient to elaborate on their concerns.\n   - Clarify any points of confusion and gather additional information to better understand their perspective.\n\n6. **Apologize if Necessary:**\n   - If the situation warrants it, offer a sincere apology for any misunderstanding or inconvenience.\n   - Avoid placing blame but express regret for any distress the patient may be experiencing.\n\n7. **Propose Solutions:**\n   - Work collaboratively with the patient to identify potential solutions to their concerns.\n   - Discuss options and involve the patient in decision-making when appropriate.\n\n8. **Maintain Professionalism:**\n   - Despite the patient\'s anger, maintain a professional and respectful attitude.\n   - Avoid becoming defensive or argumentative.\n\n9. **De-escalation Techniques:**\n   - If the situation becomes more tense, use de-escalation techniques such as lowering your voice and maintaining a non-threatening posture.\n\n10. **Follow-up and Documentation:**\n    - Ensure that the patient\'s concerns are appropriately addressed.\n    - Document the interaction thoroughly, including the steps taken to resolve the issue and any future plans.\n\n## Conclusion:\n\n*Successfully managing an angry patient scenario requires a combination of effective communication, empathy, and problem-solving skills. In the PLAB 2 exam, showcasing these abilities will not only contribute to a positive patient experience but also demonstrate your readiness to handle challenging situations as a healthcare professional. Practice these strategies, and you\'ll be well-prepared to navigate through the complexities of patient interactions in the exam and real-world clinical settings. Good luck!*`
    },
    // --- MEDICAL ERRORS ---
    {
      id: "medical-errors",
      title: "Medical Errors",
      color: "#F1C40F", // Sunflower Yellow
      content: `# MEDICAL ERRORS\n\nhttps://www.youtube.com/watch?v=H5fz4iCR504\n\n---\n\n## Addressing Medical Errors: A General Guideline\n\n1. **C - Communication:**\n   - Inform the patient: Be transparent and honest with the patient or their family about the error. Clearly communicate what happened and its potential consequences.\n   - Notify the healthcare team: Inform other relevant healthcare professionals involved in the patient\'s care.\n\n2. **O - Own Up to the Mistake:**\n   - Accept responsibility: Acknowledge the mistake and take ownership of it. Avoid blaming others or deflecting responsibility.\n   - Apologize: Offer a sincere and empathetic apology to the patient. Express regret and compassion.\n\n3. **N - Notify the Authorities:**\n   - Report to appropriate channels: Follow institutional protocols for reporting medical errors. This may involve notifying supervisors, risk management, or other relevant departments.\n\n4. **E - Evaluate and Investigate:**\n   - Conduct a thorough review: Investigate the circumstances leading to the error. Understand the root cause to prevent similar incidents in the future.\n   - Involve a multidisciplinary team: Collaborate with colleagues, including those not directly involved in the error, to gain different perspectives.\n\n5. **S - Support and Solutions:**\n   - Provide support to the patient: Offer ongoing support to the patient and their family, addressing their emotional and physical needs.\n   - Implement solutions: Develop and implement corrective actions to prevent similar errors. This may involve changes in protocols, additional training, or other quality improvement measures.`
    },
    // --- PROCEDURE STATION + EXAMPLE ---
    {
      id: "procedure-station-example",
      title: "Procedure Station + Example",
      color: "#2980B9", // Belize Hole Blue
      content: `# PROCEDURE STATION + EXAMPLE\n\n---\n\nhttps://www.youtube.com/watch?v=sYLse8_ZXPU\n\n## I. Introduction and Patient Interaction:\n\n1. **Greet the Patient:**\n   - Begin by introducing yourself to the patient in a professional and courteous manner.\n\n2. **Explain the Procedure:**\n   - Clearly explain the purpose and steps of the IV cannulation procedure to the patient, ensuring they understand and provide consent.\n\n## II. Gather Equipment:\n\n1. **Assemble Equipment:**\n   - Gather all the necessary equipment, including an IV cannula, sterile gloves, antiseptic solution, sterile dressing, tourniquet, disposable syringe, and a secure IV line.\n\n2. **Select Appropriate Cannula Size:**\n   - Choose an appropriate size of the IV cannula based on the patient\'s condition, the purpose of the IV line, and the characteristics of the chosen vein.\n\n## III. Patient Positioning:\n\n1. **Select a Suitable Site:**\n   - Identify a suitable vein for cannulation. Common sites include the back of the hand, forearm, or antecubital fossa.\n\n2. **Position the Patient:**\n   - Ensure the patient is comfortable and positioned in a way that provides easy access to the selected site.\n\n## IV. Hand Hygiene and Gloving:\n\n1. **Wash Hands:**\n   - Perform thorough hand hygiene by washing hands with soap and water or using an alcohol-based hand sanitizer.\n\n2. **Don Sterile Gloves:**\n   - Put on sterile gloves to maintain asepsis during the procedure.\n\n## V. Vein Assessment:\n\n1. **Apply Tourniquet:**\n   - Apply a tourniquet proximal to the chosen site to enhance venous distension.\n\n2. **Assess Vein:**\n   - Inspect and palpate the vein to assess its size, depth, and suitability for cannulation.\n\n## VI. Preparation of Cannula:\n\n1. **Open Cannula Package:**\n   - Open the sterile package containing the IV cannula, ensuring sterility is maintained.\n\n2. **Attach Cannula to Syringe:**\n   - Attach the cannula to a disposable syringe to flush the cannula before insertion.\n\n## VII. Insertion of Cannula:\n\n1. **Insert the Cannula:**\n   - Hold the cannula at a shallow angle (around 15-30 degrees) to the skin and insert it into the vein with a smooth, controlled motion.\n\n2. **Check for Flashback:**\n   - Observe for a flashback of blood in the chamber of the cannula to confirm proper placement within the vein.\n\n## VIII. Secure the Cannula:\n\n1. **Advance the Cannula:**\n   - Gently advance the cannula into the vein while maintaining control of the needle.\n\n2. **Release Tourniquet:**\n   - Release the tourniquet once the cannula is in place and blood flow is confirmed.\n\n3. **Remove Needle:**\n   - Withdraw the needle smoothly while keeping the cannula in place.\n\n4. **Secure Cannula in Place:**\n   - Secure the cannula in place using an adhesive dressing.\n\n## IX. Connect IV Line:\n\n1. **Attach IV Line:**\n   - Connect the IV line to the cannula and secure it with a dressing.\n\n2. **Flush the Line:**\n   - Flush the line with a sterile saline solution to ensure patency.\n\n## X. Documentation and Post-Procedure Care:\n\n1. **Document the Procedure:**\n   - Record details of the IV cannulation procedure, including the site, size of the cannula, any complications, and patient response.\n\n2. **Provide Post-Procedure Care:**\n   - Monitor the patient for any signs of complications, such as swelling or pain at the cannulation site.\n\n## XI. Closing:\n\n1. **Thank the Patient:**\n   - Express gratitude for the patient\'s cooperation during the procedure.\n\n2. **Ensure Comfort:**\n   - Ensure the patient is comfortable and informed about post-procedure care.`
    },
    // --- CLINCHERS ---
    {
      id: "clinchers-all",
      title: "Clinchers - Differential Diagnoses",
      color: "#9B59B6", // Purple
      content: `# CLINCHERS - SPECIALTY DIFFERENTIAL DIAGNOSES (PART 1)

## CARDIOLOGY

### Chest Pain
- **ACS (Acute Coronary Syndrome)**: *Central, crushing, radiates to jaw/arm, exertional, sweating, risk factors (DM, HTN, smoking, family Hx)*
- **PE (Pulmonary Embolism)**: *Sudden onset, pleuritic, SOB, tachycardia, risk factors (OCPs, recent surgery/immobility), low O2 sats*
- **Pericarditis**: *Sharp, positional (worse lying flat, better leaning forward), pericardial rub, recent viral illness*
- **Tietze Syndrome**: *Localized chest wall tenderness, reproducible with palpation, no systemic symptoms*
- **Shingles**: *Unilateral, dermatomal distribution, prodromal pain before rash appears, vesicular rash*
- **GERD**: *Burning, worse after meals/lying flat, relieved by antacids*
- **Aortic Dissection**: *Tearing pain, sudden onset, radiates to back, HTN, Marfan's syndrome*

### Mnemonic: "PARTERS"
- **P**ulmonary embolism
- **A**ortic dissection
- **R**upture of esophagus
- **T**ension pneumothorax
- **E**sophageal spasm
- **R**eflux/GERD
- **S**keletal/muscle pain

### Palpitations
- **Arrhythmia**: *Sudden onset, irregular, previous episodes, family history*
- **Atrial Fibrillation**: *Irregular pulse, SOB, dizziness, risk factors (age, HTN, valve disease)*
- **Anxiety**: *Associated with stressful situations, other anxiety symptoms*
- **Thyrotoxicosis**: *Weight loss, heat intolerance, tremor, exophthalmos*
- **Drugs/Stimulants**: *Caffeine, alcohol, recreational drugs, medication side effects*

### Lower Limb Symptoms
- **PAD (Peripheral Arterial Disease)**: *Intermittent claudication, cold extremities, decreased pulses, risk factors (smoking, DM, HTN)*
- **Venous Insufficiency**: *Aching pain, worse at end of day, varicose veins, edema improving with elevation*
- **DVT**: *Unilateral leg swelling, pain, warmth, risk factors (immobility, OCPs, cancer)*
- **Acute Limb Ischemia**: *6 Ps - Pain, Pallor, Pulselessness, Paresthesia, Paralysis, Perishing cold*

### Heart Failure
- **Left-sided**: *Exertional dyspnea, orthopnea, PND, pulmonary edema*
- **Right-sided**: *Peripheral edema, elevated JVP, hepatomegaly, ascites*
- **Post-MI**: *History of MI, sudden onset SOB, signs of left ventricular failure*

## NEUROLOGY

### Headache
- **Migraine**: *One-sided, photophobia, phonophobia, nausea, aura, family history*
- **Tension Headache**: *Bilateral, band-like, stress-related, no neurological symptoms*
- **SAH (Subarachnoid Hemorrhage)**: *"Thunderclap" (worst ever), sudden onset, neck stiffness, altered consciousness*
- **GCA (Giant Cell Arteritis)**: *Elderly, temporal headache, jaw claudication, visual disturbances, high ESR/CRP*
- **Cluster Headache**: *Male predominance, severe periorbital pain, lacrimation, nasal congestion, periodicity*
- **Trigeminal Neuralgia**: *Paroxysmal, electric-shock like, triggered by touch/washing/eating, brief episodes*
- **Hangover**: *History of alcohol, relief with hydration/analgesics, other hangover symptoms*

### Neurological Deficit
- **Stroke/TIA**: *Sudden onset, focal neurological deficit, vascular risk factors, resolves <24h in TIA*
- **MS (Multiple Sclerosis)**: *Young adults, relapsing-remitting, optic neuritis, sensory changes, MRI shows plaques*
- **GBS (Guillain-Barré Syndrome)**: *Ascending paralysis, post-infection, areflexia, albuminocytologic dissociation in CSF*
- **Head Injury**: *History of trauma, LOC, amnesia, vomiting, focal deficits*
- **Encephalitis**: *Fever, altered mental status, seizures, headache, recent viral illness*
- **Cerebellar Ataxia**: *Gait disturbance, dysmetria, intention tremor, nystagmus, slurred speech*

### Seizures
- **Epilepsy**: *Recurrent, stereotypical, family history, EEG changes*
- **Febrile Seizures (Pediatric)**: *Age 6mo-5yr, associated with fever, self-limiting*
- **Alcohol Withdrawal**: *History of alcohol use, tremor, delirium, autonomic hyperactivity*
- **Space-Occupying Lesion**: *Headache, focal neurological deficits, raised ICP symptoms*
- **Metabolic**: *Hypoglycemia, hyponatremia, hypocalcemia, check electrolytes*

### Neuropathy
- **Diabetic**: *Glove and stocking distribution, burning pain, long-standing diabetes*
- **Alcohol-related**: *Burning feet, altered proprioception, history of alcohol excess*
- **B12 Deficiency**: *Sensory ataxia, subacute combined degeneration, megaloblastic anemia*
- **Post-herpetic Neuralgia**: *Pain in dermatome of previous shingles, vesicular rash history*
- **Carpal Tunnel Syndrome**: *Median nerve compression, wrist pain, worse at night, pregnancy risk*

## RESPIRATORY

### Cough
- **Asthma**: *Wheezing, nocturnal, exercise-induced, reversible*
- **COPD**: *Smoker, productive cough, progressive SOB, not fully reversible*
- **Pneumonia**: *Fever, productive cough, focal chest signs, consolidation on X-ray*
- **Lung Cancer**: *Smoker, hemoptysis, weight loss, persistent/changing cough, risk after 40*
- **TB**: *Night sweats, weight loss, hemoptysis, risk factors (immunosuppression, endemic area, contact)*
- **PCP**: *HIV risk factors, dry cough, progressive SOB, bilateral ground glass on CXR*
- **Bronchiectasis**: *Copious sputum, recurrent infections, coarse crackles, history of childhood infections*

### Shortness of Breath
- **Asthma**: *Wheezing, trigger factors, reversible*
- **COPD**: *Smoking history, chronic, limited reversibility*
- **PE**: *Sudden onset, pleuritic chest pain, risk factors (immobility, OCPs, surgery)*
- **Pneumonia**: *Fever, productive cough, focal chest signs*
- **Heart Failure**: *Orthopnea, PND, peripheral edema, raised JVP*
- **Pneumothorax**: *Sudden onset, pleuritic pain, unilateral reduced breath sounds*
- **ARDS**: *Rapid respiratory deterioration, bilateral infiltrates, precipitating condition*
- **Anaphylaxis**: *Acute SOB, urticaria, angioedema, hypotension, allergen exposure*

### Sleep Disturbance
- **OSA (Obstructive Sleep Apnea)**: *Snoring, witnessed apneas, morning headaches, daytime somnolence, obesity*
- **COPD**: *Nocturnal hypoxemia, early morning headaches, smoker*
- **Heart Failure**: *Orthopnea, PND, peripheral edema*

### Hemoptysis
- **Bronchitis**: *Small volume, associated with infection*
- **Lung Cancer**: *Risk factors (smoking, age), weight loss, persistent symptoms*
- **TB**: *Night sweats, weight loss, exposure history*
- **Pneumonia**: *Fever, productive cough, focal consolidation*
- **Pulmonary Embolism**: *Sudden onset, pleuritic pain, risk factors*
- **Bronchiectasis**: *History of recurrent infections, copious sputum*

---

# CLINCHERS - SPECIALTY DIFFERENTIAL DIAGNOSES (PART 2)

## RHEUMATOLOGY

### Joint Pain
- **Osteoarthritis**: *Older age, weight-bearing joints, morning stiffness <30min, pain with activity*
- **Rheumatoid Arthritis**: *Symmetrical small joints, prolonged morning stiffness, systemic symptoms*
- **Gout**: *Monoarticular (often 1st MTP), sudden onset, severe pain, history of high purine diet/alcohol*
- **Reactive Arthritis**: *Young adults, asymmetric oligoarthritis, recent GI/GU infection, conjunctivitis, urethritis*
- **PMR (Polymyalgia Rheumatica)**: *Elderly, proximal muscle pain/stiffness, high ESR/CRP, responds to steroids*
- **Septic Arthritis**: *Single hot swollen joint, fever, systemically unwell, elevated WBC*
- **Psoriatic Arthritis**: *Asymmetric, dactylitis, nail changes, psoriatic plaques*

### Back Pain
- **Mechanical**: *Related to activity, improves with rest, no red flags*
- **Disc Herniation**: *Radicular pain, positive straight leg raise, neurological deficits*
- **Cauda Equina**: *Saddle anesthesia, bladder/bowel dysfunction, bilateral symptoms - EMERGENCY*
- **Malignancy**: *Age >50, night pain, weight loss, previous cancer*
- **Ankylosing Spondylitis**: *Young males, inflammatory back pain, morning stiffness, improves with exercise*
- **AAA (Abdominal Aortic Aneurysm)**: *Pulsatile abdominal mass, hypotension, elderly with risk factors*

### Fatigue
- **CFS (Chronic Fatigue Syndrome)**: *Post-viral, persistent >6 months, post-exertional malaise, normal tests*
- **Depression**: *Low mood, anhedonia, sleep disturbance, poor concentration*
- **Hypothyroidism**: *Weight gain, cold intolerance, hair loss, constipation, dry skin*
- **Anemia**: *Pallor, shortness of breath on exertion, tachycardia*
- **PMR**: *Older adults, proximal muscle pain, morning stiffness, high ESR*
- **Sleep Apnea**: *Daytime somnolence, obesity, snoring, unrefreshing sleep*

### Falls (Elderly)
- **Postural Hypotension**: *Dizziness on standing, medication-related, dehydration*
- **Vasovagal Syncope**: *Prodromal symptoms, triggered by specific situations*
- **Arrhythmia**: *No warning, sudden loss of consciousness, cardiac history*
- **Stroke/TIA**: *Focal neurological symptoms, vascular risk factors*
- **Hip Fracture**: *Pain, inability to bear weight, external rotation of leg*
- **Medication-related**: *Sedatives, antihypertensives, antipsychotics*

## GASTROENTEROLOGY

### Dyspepsia
- **GERD**: *Burning, worse after meals/lying flat, relieved by antacids*
- **PUD (Peptic Ulcer Disease)**: *Epigastric pain, relieved by food (duodenal) or worse with food (gastric), H. pylori*
- **Gastritis**: *Epigastric pain, nausea, NSAID use, alcohol*
- **Esophageal Cancer**: *Progressive dysphagia, weight loss, age >50*
- **Barrett's Esophagus**: *Long-standing GERD, columnar metaplasia, pre-malignant*
- **Functional Dyspepsia**: *Chronic symptoms, normal investigations, stress-related*

### Dysphagia
- **Esophageal Cancer**: *Progressive, solids then liquids, weight loss, smoking/alcohol history*
- **Achalasia**: *Progressive, solids and liquids, regurgitation of undigested food*
- **Esophageal Stricture**: *History of GERD/radiotherapy, gradually progressive*
- **External Compression**: *Mediastinal masses, goiter*
- **Neurological**: *Stroke, Parkinson's, MND, symptoms of underlying condition*
- **Pharyngeal Pouch**: *Elderly, regurgitation of undigested food, gurgling in neck*

### Abdominal Pain
- **Appendicitis**: *RIF pain migrating from periumbilical, anorexia, fever, rebound tenderness*
- **Cholecystitis**: *RUQ pain, Murphy's sign, fatty food intolerance, fever*
- **Diverticulitis**: *LIF pain, fever, altered bowel habit, previous episodes*
- **Intestinal Obstruction**: *Colicky pain, vomiting, distension, constipation, previous surgery*
- **IBD**: *Chronic, diarrhea, weight loss, extraintestinal manifestations*
- **IBS**: *Chronic, relation to stress, no alarm features, Rome criteria*
- **Pancreatitis**: *Severe epigastric pain radiating to back, vomiting, history of gallstones/alcohol*

### Diarrhea
- **Acute Infectious**: *Fever, vomiting, self-limiting, contact history*
- **IBD**: *Blood/mucus, weight loss, nocturnal, abdominal pain, extraintestinal features*
- **IBS**: *Chronic, relation to stress, no alarm features, alternating with constipation*
- **Celiac Disease**: *Steatorrhea, weight loss, family history, associated autoimmune conditions*
- **Malabsorption**: *Steatorrhea, weight loss, nutritional deficiencies*
- **Colorectal Cancer**: *Change in bowel habit, weight loss, blood in stool, age >50*
- **Microscopic Colitis**: *Chronic watery diarrhea, normal endoscopy, histological diagnosis*

### GI Bleeding
- **Upper GI**: *Hematemesis, melena, NSAID use, peptic ulcer, varices*
- **Lower GI**: *Fresh blood PR, diverticular disease, hemorrhoids, IBD, colorectal cancer*
- **Hematemesis**: *PUD, esophageal varices, Mallory-Weiss tear, gastritis*
- **Melena**: *Upper GI bleed, small bowel source*
- **Hematochezia**: *Lower GI bleed, hemorrhoids, diverticular disease, IBD, polyps/cancer*
- **Angiodysplasia**: *Elderly, painless, may be recurrent*

### Abnormal LFTs
- **Alcoholic Liver Disease**: *AST>ALT (2:1), GGT elevation, alcohol history*
- **Viral Hepatitis**: *Prodromal symptoms, jaundice, risk factors (travel, MSM, IDU)*
- **NAFLD/NASH**: *Metabolic syndrome, obesity, diabetes, mild-moderate elevation*
- **Drug-Induced**: *Recent medication changes, rapid onset, mixed pattern*
- **Hemochromatosis**: *Elevated ferritin/transferrin saturation, skin pigmentation, diabetes, arthralgia*
- **Gilbert's Syndrome**: *Isolated unconjugated hyperbilirubinemia, benign, exacerbated by fasting/stress*
- **Primary Biliary Cholangitis**: *Female predominance, pruritus, elevated ALP/GGT*

### Alcohol-Related Problems
- **Alcoholic Hepatitis**: *Jaundice, tender hepatomegaly, elevated AST>ALT*
- **Cirrhosis**: *Jaundice, ascites, splenomegaly, encephalopathy, varices*
- **Alcoholic Foot**: *Painful peripheral neuropathy, burning sensation*
- **Wernicke-Korsakoff**: *Confusion, ataxia, ophthalmoplegia, confabulation*
- **Withdrawal**: *Tremor, seizures, hallucinations, autonomic hyperactivity*

## RENAL

### Hematuria
- **UTI**: *Dysuria, frequency, suprapubic tenderness, positive dipstick*
- **Stones**: *Colicky pain, previous episodes, family history*
- **Malignancy**: *Age >50, painless, smoking history, occupational exposure*
- **Glomerulonephritis**: *Proteinuria, hypertension, edema, RBC casts*
- **BPH**: *Elderly male, LUTS, gradually progressive*
- **Polycystic Kidney Disease**: *Family history, palpable kidneys, hypertension*

### Renal Failure
- **Pre-Renal**: *Dehydration, hypotension, high urea:creatinine ratio, improved with fluid*
- **Intrinsic Renal**: *Glomerulonephritis, ATN (sepsis, nephrotoxins), persistent despite fluid*
- **Post-Renal**: *Obstruction, palpable bladder, anuria, hydronephrosis on imaging*
- **AKI**: *Rapid rise in creatinine, oliguria, reversible causes*
- **CKD**: *Gradual decline, small kidneys on ultrasound, anemia, mineral bone disease*
- **Analgesic Nephropathy**: *History of NSAID use, papillary necrosis*

### Electrolyte Disturbances
- **Hyponatremia**: *SIADH, diuretics, heart failure, cirrhosis, Addison's*
- **Hyperkalemia**: *Renal failure, medications (ACEi/ARB, K-sparing diuretics), Addison's*
- **Hypercalcemia**: *Malignancy, hyperparathyroidism, myeloma, sarcoidosis, "bones, stones, abdominal groans"*
- **Hypokalemia**: *Diuretics, vomiting, diarrhea, renal tubular acidosis*
- **Hypernatremia**: *Dehydration, diabetes insipidus, iatrogenic*

### Urinary Symptoms
- **UTI**: *Dysuria, frequency, urgency, suprapubic pain, fever*
- **BPH**: *Hesitancy, poor stream, dribbling, nocturia*
- **Urethritis**: *Dysuria, discharge, STI risk factors*
- **Prostatitis**: *Perineal pain, fever, dysuria, tender prostate*
- **Interstitial Cystitis**: *Chronic pelvic pain, frequency, negative cultures*

---

# CLINCHERS - SPECIALTY DIFFERENTIAL DIAGNOSES (PART 3)

## ENDOCRINOLOGY

### Thyroid Disorders
- **Hypothyroidism**: *Fatigue, weight gain, cold intolerance, dry skin, constipation, raised TSH, low T4*
- **Hyperthyroidism**: *Weight loss, heat intolerance, palpitations, anxiety, tremor, low TSH, high T4/T3*
- **Thyroiditis**: *Painful thyroid, transient hyperthyroidism followed by hypothyroidism*
- **Thyroid Nodule**: *Solitary nodule, hoarse voice, dysphagia, family history*
- **Thyroid Storm**: *Fever, tachycardia, agitation, delirium, precipitating illness*

### Parathyroid Disorders
- **Hyperparathyroidism**: *High calcium, low phosphate, "bones, stones, abdominal groans, psychic moans"*
- **Hypoparathyroidism**: *Low calcium, high phosphate, tetany, seizures, post-thyroidectomy*

### Adrenal Disorders
- **Cushing's**: *Central obesity, striae, hypertension, diabetes, muscle weakness, moon face*
- **Addison's**: *Hyperpigmentation, salt craving, weight loss, postural hypotension, hyponatremia*
- **Pheochromocytoma**: *Paroxysmal hypertension, sweating, palpitations, headaches, anxiety*

### Pituitary Disorders
- **Hyperprolactinemia**: *Galactorrhea, amenorrhea, infertility, visual field defects*
- **Acromegaly**: *Enlarged hands/feet, coarse facial features, headache, sleep apnea*
- **Hypopituitarism**: *Multiple hormone deficiencies, fatigue, amenorrhea, hypotension*
- **Diabetes Insipidus**: *Polyuria, polydipsia, dilute urine, normal glucose*

### Diabetes
- **Type 1**: *Younger onset, autoimmune, absolute insulin deficiency*
- **Type 2**: *Older onset, obesity, insulin resistance, relative insulin deficiency*
- **LADA**: *Adult-onset but autoimmune features*
- **MODY**: *Young onset, family history, non-insulin dependent*
- **Gestational**: *During pregnancy, risk factors (obesity, family history)*

## HEMATOLOGY

### Anemia
- **Iron Deficiency**: *Microcytic, low ferritin, menorrhagia/GI blood loss, koilonychia*
- **B12/Folate Deficiency**: *Macrocytic, neurological symptoms, glossitis, vegetarian/vegan diet*
- **Hemolytic**: *Jaundice, high LDH, reticulocytosis, splenomegaly*
- **Anemia of Chronic Disease**: *Normocytic, underlying chronic condition, normal/high ferritin*
- **Thalassemia**: *Microcytic, normal iron studies, family history, ethnicity*
- **Aplastic Anemia**: *Pancytopenia, bone marrow failure, drugs, infection*

### Bleeding Disorders
- **ITP**: *Thrombocytopenia, petechiae/purpura, recent viral infection, normal coagulation*
- **Hemophilia**: *Male, family history, hemarthrosis, prolonged aPTT*
- **Von Willebrand**: *Mucocutaneous bleeding, family history, prolonged bleeding time*
- **DIC**: *Severe bleeding, underlying sepsis/malignancy, abnormal clotting screen*
- **Liver Disease**: *Coagulopathy, bleeding risk, thrombocytopenia, prolonged PT*
- **Anticoagulant-related**: *Therapeutic/supratherapeutic levels, recent initiation*

### Lymphadenopathy
- **Infection**: *Localized, tender, recent infection, resolves with treatment*
- **Lymphoma**: *Painless, progressive, B symptoms (night sweats, weight loss, fever), splenomegaly*
- **Leukemia**: *Generalized, associated with other cytopenias, B symptoms*
- **Metastatic Malignancy**: *Hard, fixed, underlying primary, other symptoms*
- **HIV**: *Generalized, risk factors, associated symptoms*
- **Sarcoidosis**: *Hilar, constitutional symptoms, raised ACE*

### Myeloma
- **CRAB symptoms**: *Hypercalcemia, Renal failure, Anemia, Bone lesions*
- **Bence Jones Protein**: *Light chains in urine*
- **Pathological Fractures**: *Bone pain, lytic lesions*
- **Recurrent Infections**: *Immunoparesis*
- **Rouleaux Formation**: *High ESR, increased plasma viscosity*

## SURGERY/UROLOGY

### Abdominal Pain
- **Appendicitis**: *RIF pain migrating from periumbilical, anorexia, nausea/vomiting, rebound tenderness*
- **Cholecystitis**: *RUQ pain, Murphy's sign, fatty food intolerance*
- **Diverticulitis**: *LIF pain, fever, altered bowel habit, tenderness*
- **Bowel Obstruction**: *Colicky, vomiting, distension, constipation, previous surgery*
- **Perforated Viscus**: *Sudden severe pain, rigid abdomen, pneumoperitoneum*
- **Ischemic Bowel**: *Out of proportion pain, atherosclerosis risk factors, bloody diarrhea*
- **Pancreatitis**: *Epigastric pain radiating to back, nausea/vomiting, elevated amylase/lipase*

### Scrotal Swelling
- **Hydrocele**: *Painless, transilluminates, cannot get above it*
- **Epididymo-orchitis**: *Pain, fever, urinary symptoms, leukocytosis*
- **Testicular Torsion**: *Sudden severe pain, high-riding testicle, absent cremasteric reflex, surgical emergency*
- **Varicocele**: *"Bag of worms," mainly left-sided, disappears when supine*
- **Testicular Cancer**: *Painless hard lump, does not transilluminate, risk factors (cryptorchidism)*
- **Inguinal Hernia**: *Can get above it, reducible, cough impulse*

### Urinary Symptoms
- **UTI**: *Dysuria, frequency, urgency, positive urine dipstick*
- **BPH**: *Nocturia, hesitancy, weak stream, post-void dribbling, palpable bladder*
- **Prostate Cancer**: *Hard nodule on DRE, elevated PSA, usually asymptomatic early*
- **Urethral Stricture**: *Poor stream, urinary retention, history of trauma/STI*
- **Stone Disease**: *Colicky pain, hematuria, previous episodes*
- **Detrusor Instability**: *Urgency, frequency, urge incontinence*

### Neck Lumps
- **Thyroid Nodule**: *Moves on swallowing, anterior midline, thyroid function tests*
- **Lymphadenopathy**: *Discrete, mobile/fixed, tender/non-tender*
- **Branchial Cyst**: *Lateral neck, fluctuant, young adults*
- **Thyroglossal Cyst**: *Midline, elevates on tongue protrusion*
- **Sebaceous Cyst**: *Superficial, skin attachment, expressible material*
- **Laryngocele**: *Air-filled, increases with Valsalva*

## ORTHOPEDICS

### Joint Pain
- **Osteoarthritis**: *Weight-bearing joints, morning stiffness <30min, crepitus, worsens with activity*
- **Rheumatoid Arthritis**: *Symmetrical small joints, prolonged morning stiffness, systemic symptoms*
- **Gout**: *Monoarticular (1st MTP), sudden onset, severe pain, elevated uric acid*
- **Septic Arthritis**: *Acutely hot swollen joint, fever, elevated WBC, surgical emergency*
- **Reactive Arthritis**: *Post-infectious, asymmetric large joints, urethritis, conjunctivitis*
- **Psoriatic Arthritis**: *Asymmetric, dactylitis (sausage digits), nail pitting*
- **Fibromyalgia**: *Widespread pain, tender points, fatigue, sleep disturbance*

### Hand/Wrist Pain
- **Carpal Tunnel Syndrome**: *Tingling/numbness in median nerve distribution, worse at night/with activity, Tinel's/Phalen's positive*
- **De Quervain's Tenosynovitis**: *Pain over radial styloid, worse with thumb movement, positive Finkelstein test*
- **Trigger Finger**: *Catching/locking of finger, nodule in flexor tendon*
- **Osteoarthritis**: *DIP/PIP joints, Heberden's/Bouchard's nodes*
- **Rheumatoid Arthritis**: *MCP/PIP joints, ulnar deviation, swan-neck deformities*

### Raynaud's Phenomenon
- *Cold-induced color changes (white→blue→red), fingers/toes, associated with autoimmune conditions*
- **Primary**: *No underlying cause, usually bilateral, good prognosis*
- **Secondary**: *Associated with CTD, may have digital ulceration*

## OBGYN

### Vaginal Bleeding
- **Pregnancy-Related**: *Miscarriage, ectopic, placenta previa*
- **Menstrual Disorders**: *Menorrhagia, polymenorrhea, intermenstrual bleeding*
- **Malignancy**: *Cervical, endometrial, post-menopausal bleeding*
- **Hormonal**: *PCOS, thyroid disorders, contraceptive side effects*
- **Infection**: *PID, cervicitis*
- **Fibroids**: *Heavy periods, pelvic pressure, enlarged uterus*

### Pelvic Pain
- **Ectopic Pregnancy**: *Amenorrhea, abdominal pain, vaginal bleeding, positive pregnancy test*
- **Miscarriage**: *Vaginal bleeding, cramping, positive pregnancy test*
- **PID**: *Bilateral pain, cervical motion tenderness, fever, vaginal discharge*
- **Endometriosis**: *Cyclical pain, dyspareunia, infertility*
- **Ovarian Torsion**: *Sudden unilateral pain, nausea/vomiting, surgical emergency*
- **Ovarian Cyst**: *Unilateral pain, can be asymptomatic, rupture causes acute pain*
- **Fibroids**: *Menorrhagia, dysmenorrhea, pelvic pressure*

### Pregnancy Concerns
- **Pre-eclampsia**: *Hypertension, proteinuria, edema, headache, visual disturbance*
- **Gestational Diabetes**: *Risk factors (obesity, family history), polyhydramnios, large for dates*
- **PROM**: *Clear vaginal fluid, decreased fetal movements, risk of infection*
- **Placental Abruption**: *Painful vaginal bleeding, uterine tenderness, fetal distress*
- **Hyperemesis Gravidarum**: *Severe nausea/vomiting, weight loss, dehydration, electrolyte imbalance*
- **Rh Incompatibility**: *Rh-negative mother, risk of hemolytic disease of newborn*
- **Rubella**: *Risk of congenital rubella syndrome if infected in first trimester*

### Postpartum Concerns
- **PPH (Postpartum Hemorrhage)**: *Excessive bleeding, uterine atony, retained placenta*
- **Postpartum Depression**: *Low mood, poor bonding, anhedonia, sleep disturbance*
- **Mastitis**: *Breast pain, erythema, fever, breastfeeding difficulties*
- **Endometritis**: *Fever, uterine tenderness, offensive lochia*
- **Uterine Inversion**: *Shock, severe pain, visible/palpable mass*

### PCOS
- *Oligomenorrhea/amenorrhea, hirsutism, acne, infertility*
- *Raised LH:FSH ratio, polycystic ovaries on ultrasound*
- *Increased risk of metabolic syndrome, diabetes, cardiovascular disease*

---

# CLINCHERS - SPECIALTY DIFFERENTIAL DIAGNOSES (PART 4)

## PEDIATRICS

### Fever in Children
- **Viral URTI**: *Coryza, mild fever, cough, systemically well*
- **UTI**: *Fever, irritability, vomiting, poor feeding*
- **Meningitis**: *Fever, neck stiffness, photophobia, irritability, bulging fontanelle*
- **Pneumonia**: *Fever, tachypnea, recession, decreased air entry/crackles*
- **Febrile Convulsion**: *Age 6mo-5yr, brief generalized seizure, rapid recovery*
- **Otitis Media**: *Ear pain, fever, irritability, red bulging tympanic membrane*

### Developmental Concerns
- **Normal Variation**: *Within 2SD of median, other milestones appropriate*
- **Global Developmental Delay**: *Delay across multiple domains, consider genetic/metabolic*
- **Autism**: *Social communication deficits, repetitive behaviors, early onset*
- **Specific Learning Disability**: *Isolated difficulties, normal IQ, specific interventions*
- **Cerebral Palsy**: *Motor disorder, often with history of perinatal problems*
- **ADHD**: *Attention deficits, hyperactivity, impulsivity, functional impairment*

### Abdominal Pain
- **Appendicitis**: *Periumbilical→RIF, anorexia, fever, localized tenderness*
- **Intussusception**: *Paroxysmal pain, redcurrant jelly stool, sausage-shaped mass*
- **Constipation**: *Intermittent pain, infrequent hard stools, fecal loading*
- **Gastroenteritis**: *Vomiting, diarrhea, fever, contact history*
- **Pyloric Stenosis**: *Projectile vomiting, 2-8 weeks old, palpable olive, visible peristalsis*
- **Mesenteric Adenitis**: *RIF pain, fever, preceding URTI*

### Behavioral Concerns
- **Normal Development**: *Age-appropriate behavior, responsive to routine/boundaries*
- **ADHD**: *Hyperactivity, impulsivity, inattention, school difficulties*
- **Anxiety**: *Excessive worries, somatic complaints, school refusal*
- **Conduct Disorder**: *Persistent rule-breaking, aggression, lack of remorse*
- **Abuse/Neglect**: *Inconsistent story, inappropriate behavior, failure to thrive*
- **Night Terrors**: *Partial arousal, screaming, no memory of event*
- **Enuresis**: *Primary (never dry), secondary (after period of dryness), psychological/organic*

### Non-Accidental Injury
- *Unexplained/inconsistent injuries*
- *Delay in seeking medical attention*
- *Multiple injuries at different stages of healing*
- *Injuries in non-mobile children*
- *Uncommon injury patterns (e.g., spiral fractures, cigarette burns)*

## ENT

### Ear Pain
- **Otitis Externa**: *Pain with manipulation of pinna, discharge, canal edema*
- **Otitis Media**: *Bulging tympanic membrane, fever, preceded by URTI*
- **Mastoiditis**: *Post-auricular swelling/tenderness, displaced pinna, fever*
- **Cholesteatoma**: *Painless discharge, hearing loss, attic perforation*
- **Referred Pain**: *TMJ dysfunction, dental pathology, pharyngitis*
- **Foreign Body**: *Unilateral, child, potential witness, sudden onset*

### Hearing Loss
- **Conductive**: *External ear pathology, otitis media, otosclerosis, normal Rinne, lateralized Weber*
- **Sensorineural**: *Age-related, noise exposure, Meniere's, positive Rinne, contralateral Weber*
- **Acoustic Neuroma**: *Unilateral, tinnitus, balance problems, progressive*
- **Sudden**: *Viral, vascular, autoimmune, medical emergency*
- **Wax Impaction**: *Gradual, visible on otoscopy, relieved with removal*

### Dizziness
- **BPPV**: *Positional, brief, rotatory, nystagmus, positive Dix-Hallpike*
- **Meniere's**: *Episodic vertigo, hearing loss, tinnitus, fullness*
- **Vestibular Neuritis**: *Acute prolonged vertigo, post-viral, no hearing loss*
- **Central Causes**: *Stroke, MS, additional neurological symptoms*
- **Medication Side Effects**: *Antihypertensives, anticonvulsants, check drug history*
- **Cervicogenic**: *Neck pain/stiffness, provoked by neck movement*

### Rhinitis
- **Allergic**: *Seasonal, triggers, itchy nose/eyes, family history*
- **Vasomotor**: *Perennial, no triggers, temperature-sensitive*
- **Infective**: *Purulent discharge, fever, facial pain*
- **Medication-Induced**: *Rebound congestion, overuse of decongestants*
- **Sinusitis**: *Facial pain/pressure, purulent discharge, post-nasal drip*

### Sore Throat
- **Viral Pharyngitis**: *Mild erythema, coryza, cough, self-limiting*
- **Bacterial Tonsillitis**: *Exudative, fever, tender lymphadenopathy, Centor criteria*
- **Glandular Fever**: *Severe tonsillitis, fatigue, lymphadenopathy, splenomegaly*
- **Quinsy (Peritonsillar Abscess)**: *Unilateral, trismus, uvula deviation, drooling*
- **GERD**: *Chronic, worse in morning, heartburn*
- **Mumps**: *Parotid swelling, fever, viral prodrome*

## OPHTHALMOLOGY

### Red Eye
- **Conjunctivitis**: *Bilateral, discharge (purulent=bacterial, watery=viral), no visual change*
- **Subconjunctival Hemorrhage**: *Painless, localized, recent coughing/straining, on anticoagulants*
- **Acute Glaucoma**: *Painful, hazy cornea, mid-dilated pupil, nausea/vomiting, emergency*
- **Uveitis**: *Photophobia, circumciliary injection, small pupil, reduced vision*
- **Keratitis**: *Pain, photophobia, contact lens use, decreased vision*
- **Foreign Body**: *Unilateral, history of injury/foreign body sensation*

### Vision Loss
- **Cataract**: *Gradual, glare, improved with pinhole*
- **Glaucoma**: *Peripheral vision loss, increased IOP, optic disc cupping*
- **AMD (Age-Related Macular Degeneration)**: *Central vision loss, distortion, age >50*
- **Retinal Detachment**: *Flashes/floaters, curtain-like field loss, sudden onset*
- **Giant Cell Arteritis**: *Sudden, elderly, headache, jaw claudication, high ESR/CRP*
- **Stroke**: *Homonymous hemianopia, other neurological symptoms*
- **Optic Neuritis**: *Painful eye movements, color desaturation, MS association*

### Diplopia
- **CN Palsy**: *Specific pattern depending on nerve, diabetes, hypertension*
- **Myasthenia Gravis**: *Fatigable, ptosis, worse at end of day*
- **Orbital Mass**: *Proptosis, limitation of eye movements, associated pain*
- **Thyroid Eye Disease**: *Proptosis, lid retraction, restriction of up/down gaze*
- **Stroke**: *Acute onset, other neurological symptoms*
- **Convergence Insufficiency**: *Near vision, improved with one eye closed*

## DERMATOLOGY

### Rash
- **Eczema**: *Itchy, flexural distribution, personal/family history of atopy*
- **Psoriasis**: *Well-demarcated plaques, silvery scale, extensor surfaces, nail changes*
- **Fungal Infection**: *Scaly, itchy, spreading peripherally with central clearing*
- **Drug Eruption**: *Temporal relation to medication, generalized*
- **Urticaria**: *Transient wheals, intensely itchy, often allergic trigger*
- **Cellulitis**: *Erythema, warmth, tenderness, systemic symptoms*
- **Impetigo**: *Honey-colored crusts, highly contagious, children*
- **Herpes Labialis**: *Grouped vesicles, tingling prodrome, recurrent*

### Skin Lesions
- **Seborrheic Keratosis**: *"Stuck on" appearance, elderly, benign*
- **Actinic Keratosis**: *Scaly, sun-exposed areas, pre-malignant*
- **Basal Cell Carcinoma**: *Pearly border, central ulceration, non-metastasizing*
- **Squamous Cell Carcinoma**: *Keratotic, indurated, can metastasize*
- **Melanoma**: *ABCDE criteria (Asymmetry, Border irregularity, Color variation, Diameter >6mm, Evolution)*
- **Sebaceous Cyst**: *Slow-growing, punctum, expressed material has foul smell*
- **Lipoma**: *Soft, mobile, subcutaneous, non-tender*

### Pruritus
- **Scabies**: *Burrows, intense nocturnal itch, family members affected*
- **Dermatitis**: *Eczematous, related to contact/irritant/atopy*
- **Systemic Disease**: *Generalized, no rash, consider liver/renal/thyroid*
- **Medication-Induced**: *Temporal relation to new medication*
- **Psychogenic**: *Excoriations, no primary lesions, psychological stressors*
- **Urticaria**: *Wheals, angioedema, allergic trigger*

### STIs
- **Chlamydia**: *Often asymptomatic, discharge, dysuria*
- **Gonorrhea**: *Purulent discharge, dysuria*
- **Syphilis**: *Painless chancre (primary), widespread rash (secondary)*
- **Genital Herpes**: *Painful vesicles, recurrent, prodromal symptoms*
- **Genital Warts**: *HPV-related, painless, cauliflower-like*
- **HIV**: *Acute seroconversion illness, later opportunistic infections*
- **Trichomoniasis**: *Green-yellow discharge, fishy odor, strawberry cervix*

### Pediatric Rashes
- **Chickenpox**: *Vesicular rash in different stages, pruritic, fever*
- **Measles**: *Koplik spots, maculopapular rash, coryza, conjunctivitis*
- **Rubella**: *Fine maculopapular rash, lymphadenopathy, arthralgia*
- **Scarlet Fever**: *Sandpaper rash, strawberry tongue, sore throat*
- **Fifth Disease**: *Slapped cheek appearance, lacy rash on trunk/limbs*
- **Hand, Foot and Mouth**: *Vesicular lesions on hands/feet/mouth, fever*

## SIMMAN EMERGENCIES

### Respiratory Emergencies
- **Asthma Exacerbation**: *Wheeze, tachypnea, accessory muscle use, poor air entry*
  - *Management: Oxygen, nebulized salbutamol/ipratropium, systemic steroids, consider magnesium/aminophylline*
- **Anaphylaxis**: *Urticaria, angioedema, stridor, wheeze, hypotension*
  - *Management: IM adrenaline, oxygen, IV fluids, antihistamines, steroids, airway management*
- **Pneumonia**: *Fever, productive cough, focal chest signs, consolidation on CXR*
  - *Management: Oxygen, antibiotics, fluid resuscitation, respiratory support if needed*

### Cardiovascular Emergencies
- **Atrial Fibrillation**: *Irregular pulse, SOB, palpitations, heart failure*
  - *Management: Rate control, rhythm control if unstable, anticoagulation*
- **Acute MI**: *Chest pain, ECG changes, raised troponin, hemodynamic effects*
  - *Management: Oxygen, aspirin, clopidogrel, anticoagulation, reperfusion (PCI/thrombolysis)*
- **Upper GI Bleed**: *Hematemesis, melena, tachycardia, hypotension*
  - *Management: IV access, fluid resuscitation, blood products, PPI, endoscopy*

### Neurological Emergencies
- **Unconscious Patient**: *AVPU/GCS assessment, ABCDE approach, identify cause*
  - *Management: Secure airway, support breathing/circulation, treat underlying cause*
- **Status Epilepticus**: *Prolonged/recurrent seizures, no recovery between*
  - *Management: ABCs, benzodiazepines, phenytoin/levetiracetam, intubation if refractory*
- **Acute Stroke**: *FAST assessment, focal neurological deficit, CT brain*
  - *Management: ABCs, thrombolysis if ischemic and within time window, stroke unit*

### Metabolic Emergencies
- **Hypoglycemia**: *Confusion, sweating, tachycardia, seizures, coma*
  - *Management: Glucose (oral if conscious, IV if unconscious), identify/treat cause*
- **DKA**: *Hyperglycemia, ketosis, metabolic acidosis, dehydration*
  - *Management: IV fluids, insulin infusion, potassium replacement, treat precipitant*
- **HHS**: *Severe hyperglycemia, hyperosmolarity, no significant ketosis*
  - *Management: Fluid resuscitation, insulin, careful electrolyte management*

### Sepsis
- **Septic Shock**: *SIRS criteria, source of infection, organ dysfunction, hypotension*
  - *Management: Sepsis Six (oxygen, IV access, cultures, antibiotics, fluids, measure lactate/urine output)*
- **UTI with Sepsis**: *Fever, confusion, hypotension, tachycardia*
  - *Management: Fluid resuscitation, antibiotics, source control*
- **Acute Confusional State in Elderly**: *Acute onset, fluctuating course, identify underlying cause*
  - *Management: Treat underlying cause (often infection, medication, metabolic)*

### Other Emergencies
- **Post-Op Bleeding**: *Tachycardia, hypotension, visible bleeding, drain output*
  - *Management: Pressure, surgical review, fluid resuscitation, blood products*
- **Acute Limb Ischemia**: *6 P's (Pain, Pallor, Pulselessness, Paresthesia, Paralysis, Perishing cold)*
  - *Management: Anticoagulation, vascular surgery, analgesia*
- **Postpartum Hemorrhage**: *Excessive bleeding post-delivery, uterine atony, retained products*
  - *Management: Uterine massage, oxytocics, bimanual compression, surgical options*

## EMERGENCY MANAGEMENT PRINCIPLES

### ABCDE Approach
- **A**irway: *Assess patency, positioning, adjuncts, definitive if needed*
- **B**reathing: *Rate, depth, effort, oxygen saturation, auscultation*
- **C**irculation: *Pulse, BP, capillary refill, fluid status, ECG*
- **D**isability: *AVPU/GCS, pupils, blood glucose, focal neurology*
- **E**xposure: *Temperature, rash, trauma, environmental factors*

### Sepsis Six
1. High-flow **O**xygen
2. Blood **C**ultures
3. IV **A**ntibiotics
4. IV **F**luids
5. Check **L**actate and Hemoglobin
6. Monitor **U**rine Output

### Shock Management
- **Hypovolemic**: *Fluid resuscitation, blood products, identify/stop bleeding*
- **Cardiogenic**: *Oxygen, treat arrhythmia, inotropes, treat cause (MI, valve, etc.)*
- **Distributive**: *Fluids, vasopressors, treat cause (sepsis, anaphylaxis)*
- **Obstructive**: *Treat cause (tension pneumothorax, cardiac tamponade, PE)*

### Pain Management
- **Analgesia Ladder**: *Simple → weak opioid → strong opioid + adjuvants*
- **Acute Pain**: *Multimodal approach, regular dosing, breakthrough*
- **Chronic Pain**: *Bio-psycho-social approach, multidisciplinary management*
- **Neuropathic Pain**: *Anticonvulsants, antidepressants, topical agents*`
    }
    // --- Add other note topics here if needed ---
  ];

  // Load user notes from local storage on mount
  useEffect(() => {
    if (user) {
      const storedNotes = localStorage.getItem(`user_notes_${user.id}`);
      if (storedNotes) {
        setUserNotes(JSON.parse(storedNotes));
      }
    }
  }, [user]);

  // Save user notes to local storage when they change
  useEffect(() => {
    if (user) {
      localStorage.setItem(`user_notes_${user.id}`, JSON.stringify(userNotes));
    }
  }, [userNotes, user]);

  // Toggle notes panel
  const toggleNotes = () => {
    setIsOpen(!isOpen);
    setSelectedNote(null);
    setIsCreatingNote(false);
  };

  // Handle note selection
  const handleSelectNote = (note: NoteTopic | UserNote) => {
    setSelectedNote(note);
    setIsCreatingNote(false);
  };

  // Get the next note in sequence
  const getNextNote = (currentNote: NoteTopic | UserNote): NoteTopic | UserNote | null => {
    // Combine all notes (topics and user notes)
    const allNotes = [...noteTopics, ...userNotes];
    
    // Find the index of the current note
    const currentIndex = allNotes.findIndex(note => note.id === currentNote.id);
    
    // If found and not the last note, return the next one
    if (currentIndex !== -1 && currentIndex < allNotes.length - 1) {
      return allNotes[currentIndex + 1];
    }
    
    // If it's the last note or not found, return null or optionally loop back to the first note
    return null; // or return allNotes[0] to loop back
  };

  // Handle navigating to the next note
  const handleNextNote = () => {
    if (selectedNote) {
      const nextNote = getNextNote(selectedNote);
      if (nextNote) {
        setSelectedNote(nextNote);
        // Reset scroll position to top
        if (noteContentRef.current) {
          noteContentRef.current.scrollTop = 0;
        }
      }
    }
  };

  // Handle creating a new note
  const handleCreateNote = () => {
    setSelectedNote(null);
    setIsCreatingNote(true);
    setNewNoteTitle('');
    setNewNoteContent('');
  };

  // Handle saving a new note
  const handleSaveNote = () => {
    if (!newNoteTitle.trim()) {
      alert('Please enter a title for your note');
      return;
    }

    const newNote: UserNote = {
      id: `user-note-${Date.now()}`,
      title: newNoteTitle,
      content: newNoteContent,
      createdAt: new Date().toISOString()
    };

    setUserNotes([...userNotes, newNote]);
    setIsCreatingNote(false);
    setSelectedNote(newNote);
  };

  // Handle deleting user note
  const handleDeleteNote = (noteId: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this note?');
    if (confirmed) {
      const updatedNotes = userNotes.filter(note => note.id !== noteId);
      setUserNotes(updatedNotes);
      setSelectedNote(null);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Only show for logged-in users
  if (!user) return null;

  // Helper function to process text formatting
  const processTextFormatting = (text: string): string => {
    // Process in specific order to handle nested formatting
    
    // Step 1: Temporarily replace triple asterisks (bold + italic)
    let processed = text.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
    
    // Step 2: Replace double asterisks/underscores (bold)
    processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    processed = processed.replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // Step 3: Replace single asterisks/underscores (italics)
    // We use a simple approach that avoids already processed content
    processed = processed.replace(/\*(.*?)\*/g, '<em>$1</em>');
    processed = processed.replace(/_(.*?)_/g, '<em>$1</em>');
    
    return processed;
  };

  return (
    <>
      {/* Notes button */}
      <button 
        className="notes-button" 
        onClick={toggleNotes}
        aria-label="Open notes"
      >
        <span className="notes-icon">📝</span>
        <span className="notes-text">N<br/>O<br/>T<br/>E<br/>S</span>
      </button>

      {/* Notes modal */}
      {isOpen && (
        <div className="notes-overlay">
          <div className="notes-modal">
            <div className="notes-header">
              <h2>{selectedNote ? selectedNote.title : 'Study Notes'}</h2>
              <button 
                className="close-button" 
                onClick={toggleNotes}
                aria-label="Close notes"
              >
                ×
              </button>
            </div>

            <div className="notes-content">
              {!selectedNote && !isCreatingNote ? (
                <>
                  <div className="notes-section">
                    <h3>Reference Topics</h3>
                    <div className="notes-topics">
                      {noteTopics.map(topic => (
                        <div 
                          key={topic.id}
                          className="note-topic-item"
                          style={{ backgroundColor: topic.color }}
                          onClick={() => handleSelectNote(topic)}
                        >
                          {topic.title}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="notes-section">
                    <div className="section-header">
                      <h3>My Notes</h3>
                      <button 
                        className="add-note-button"
                        onClick={handleCreateNote}
                      >
                        + New Note
                      </button>
                    </div>
                    {userNotes.length > 0 ? (
                      <div className="user-notes-list">
                        {userNotes.map(note => (
                          <div 
                            key={note.id}
                            className="user-note-item"
                            onClick={() => handleSelectNote(note)}
                          >
                            <div className="note-title">{note.title}</div>
                            <div className="note-date">
                              {formatDate(note.createdAt)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-notes-message">
                        You haven't created any notes yet. Click "New Note" to get started.
                      </p>
                    )}
                  </div>
                </>
              ) : isCreatingNote ? (
                <div className="note-editor">
                  <input
                    type="text"
                    placeholder="Note Title"
                    value={newNoteTitle}
                    onChange={(e) => setNewNoteTitle(e.target.value)}
                    className="note-title-input"
                  />
                  <textarea
                    placeholder="Write your note content here..."
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    className="note-content-input"
                  />
                  <div className="editor-buttons">
                    <button 
                      className="cancel-button"
                      onClick={() => setIsCreatingNote(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      className="save-button"
                      onClick={handleSaveNote}
                    >
                      Save Note
                    </button>
                  </div>
                </div>
              ) : (
                <div className="note-viewer">
                  <div className="note-content" ref={noteContentRef}>
                    {selectedNote ? (
                      // Check if it's a UserNote (has createdAt) to render as plain text
                      'createdAt' in selectedNote ? (
                        <pre>{selectedNote.content}</pre>
                      ) : (
                        // Otherwise, it's a NoteTopic, render with markdown parsing
                        <div className="markdown-content">
                          {selectedNote.content.split('\n').map((line, index) => {
                            // Check for YouTube links first
                            const youtubeRegex = /^\s*(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S*)?\s*$/;
                            const youtubeMatch = line.match(youtubeRegex);

                            if (youtubeMatch && youtubeMatch[1]) {
                              const videoId = youtubeMatch[1];
                              const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                              return (
                                <div key={index} className="video-embed-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: '100%', background: '#000', marginBottom: '1em' }}>
                                  <iframe
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                    src={embedUrl}
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                  >
                                  </iframe>
                                </div>
                              );
                            } else if (line.startsWith('<iframe>')) {
                              // Handle explicitly added iframes (like the one previously added)
                              // This is less ideal than just having the URL, but preserves existing content
                              return <div key={index} dangerouslySetInnerHTML={{ __html: line }} />;
                            } else if (line.startsWith('# ')) {
                              return <h1 key={index}>{line.slice(2)}</h1>;
                            } else if (line.startsWith('## ')) {
                              return <h2 key={index}>{line.slice(3)}</h2>;
                            } else if (line.startsWith('### ')) {
                              return <h3 key={index}>{line.slice(4)}</h3>;
                            } else if (line.startsWith('- ')) {
                              // Process the line for formatting
                              const formattedContent = processTextFormatting(line.slice(2));
                              return <li key={index} dangerouslySetInnerHTML={{ __html: formattedContent }} />;
                            } else if (line.trim() === '') {
                              return <br key={index} />;
                            } else {
                              // Process the line for formatting
                              const formattedContent = processTextFormatting(line);
                              return <p key={index} dangerouslySetInnerHTML={{ __html: formattedContent }} />;
                            }
                          })}
                        </div>
                      )
                    ) : null /* Should not happen if selectedNote is set */ }
                  </div>
                  
                  {selectedNote && 'createdAt' in selectedNote && (
                    <div className="note-actions">
                      <span className="note-date">
                        Created: {formatDate(selectedNote.createdAt)}
                      </span>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteNote(selectedNote.id)}
                      >
                        Delete Note
                      </button>
                    </div>
                  )}
                  
                  <div className="navigation-buttons">
                    <button 
                      className="back-button"
                      onClick={() => setSelectedNote(null)}
                    >
                      ← Back to Notes
                    </button>
                    {selectedNote && (
                      <button 
                        className="next-button"
                        onClick={handleNextNote}
                      >
                        Next →
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notes; 